import {action, computed, makeObservable, observable, reaction, runInAction} from 'mobx'
import {injectable} from 'inversify'
import {subscribe} from 'dna-react-ioc'
import dayjs from 'dayjs'
import {v4 as uuid} from 'uuid'
import i18next from '@/i18n'

import {getProcessingMethodsTemplate} from './constants'
import {
    ApplicationValidationResponse,
    BankDetailsFormData,
    BusinessStructure,
    CompanyContactsFormData,
    CompanyDetailsFormData,
    CompanyInfoResponse,
    CorporateOwnerData,
    DirectorData,
    DocumentUploadProgress,
    DocumentUploadResult,
    IOnboardingStore,
    MccCategory,
    OnboardingProcessStages,
    OnboardingProcessStatusResponse,
    OnboardingStep,
    OwnerData,
    StoreData,
    VerifyPersonResponse
} from '@/stores/onboarding/models/onboarding'
import {fetchCompanyInfo} from '@/stores/company-details/services'
import {getRouterStore} from '@/router/utils'
import {ROUTES} from '@/router/routes'
import {AuthStoreSymbol} from '@/stores/auth/AuthStore'
import {ProfileStoreInterface, ProfileStoreSymbol} from '@/pages/Profile'
import {AuthStoreInterface} from '@/stores'
import {
    fetchSelfOnboardingAddressByPostalCode,
    fetchSelfOnboardingApplicationDetails,
    fetchSelfOnboardingCompanyByName,
    fetchSelfOnboardingDictionary,
    fetchSelfOnboardingProcessStatus,
    saveSelfOnboardingApplication,
    startSelfOnboardingApplication,
    submitSelfOnboardingApplication,
    terminateSelfOnboardingProcess,
    validateSelfOnboardingApplication,
    verifySelfOnboardingPerson
} from './services'
import {
    getCurrentUserEmail,
    mapCompanyDetailsToStartApplication,
    mapCompanyInfoToOwnersAndDirectors,
    mapFormDataToSaveApplication
} from './services/field-mapping'
import {getGlobalLoaderStore} from '@/stores/global-loader'
import {cleanFormDataForAPI} from '@/pages/onboarding/utils'
import {ONBOARDING_STORE_SYMBOLS} from './symbols'
import {ONBOARDING_MANDATORY_FIELDS} from './constants'
import {OnboardingBankAccountStore} from '@/stores/onboarding/OnboardingBankAccountStore'
import {captureSentryError, getErrorMessage} from '@/services/sentry-helper'

@injectable()
export class OnboardingStore implements IOnboardingStore {
    isLoading = false
    isSubmitting = false
    isInitialized = false
    processStatus: string | undefined = undefined
    processStages: OnboardingProcessStages | undefined = undefined
    validationResponse: ApplicationValidationResponse | undefined = undefined

    currentStep = OnboardingStep.START
    hasExplicitStepFromNavigation = false

    companyDetails: CompanyDetailsFormData | undefined = undefined
    companyContacts: CompanyContactsFormData | undefined = undefined
    owners: OwnerData[] = []
    corporateOwners: CorporateOwnerData[] = []
    directors: DirectorData[] = []
    bankDetails: BankDetailsFormData | undefined = undefined
    stores: StoreData[] = []
    mccData: MccCategory[] = []
    isMccLoading = false

    rawApplicationData: any = undefined
    skipFetchingExistingData = false
    _originalCompanyInfo: any = undefined

    documentUploadProgress: Map<string, DocumentUploadProgress> = new Map()
    uploadedDocuments: Map<string, DocumentUploadResult> = new Map()

    ownersFormChanged: Map<string, boolean> = new Map()
    directorsFormChanged: Map<string, boolean> = new Map()

    isNavigatingFromValidation = false

    businessStructures: {code: string; value: string}[] = []

    stepCompletionStatus = {
        [OnboardingStep.START]: false,
        [OnboardingStep.COMPANY_DETAILS]: false,
        [OnboardingStep.COMPANY_CONTACTS]: false,
        [OnboardingStep.OWNERS_DIRECTORS]: false,
        [OnboardingStep.BANK_DETAILS]: false,
        [OnboardingStep.OUTLETS_INFORMATION]: false,
        [OnboardingStep.REVIEW_SUBMIT]: false,
        [OnboardingStep.APPLICATION_PROGRESS]: false
    }

    constructor() {
        makeObservable(this, {
            isLoading: observable,
            isSubmitting: observable,
            isInitialized: observable,
            processStatus: observable,
            processStages: observable,
            validationResponse: observable,
            currentStep: observable,
            hasExplicitStepFromNavigation: observable,
            companyDetails: observable,
            companyContacts: observable,
            owners: observable,
            corporateOwners: observable,
            directors: observable,
            bankDetails: observable,
            stores: observable,
            mccData: observable,
            isMccLoading: observable,
            rawApplicationData: observable,
            skipFetchingExistingData: observable,
            _originalCompanyInfo: observable,
            documentUploadProgress: observable,
            uploadedDocuments: observable,
            ownersFormChanged: observable,
            directorsFormChanged: observable,
            isNavigatingFromValidation: observable,
            businessStructures: observable,
            stepCompletionStatus: observable,

            processKey: computed,
            canProceedToNextStep: computed,
            isCurrentStepValid: computed,
            originalCompanyInfo: computed,
            isCompanyOrLLPFlow: computed,
            isSoleTraderOrPartnershipFlow: computed,
            isCompanyFlow: computed,
            isLLPFlow: computed,
            isSoleTraderFlow: computed,
            isPartnershipFlow: computed,

            goToStep: action.bound,
            setStepFromNavigation: action.bound,
            goToNextStep: action.bound,
            goToPreviousStep: action.bound,
            saveCompanyDetails: action.bound,
            saveCompanyContacts: action.bound,
            saveOwner: action.bound,
            updateOwner: action.bound,
            deleteOwner: action.bound,
            saveDirector: action.bound,
            updateDirector: action.bound,
            deleteDirector: action.bound,
            updateSignatory: action.bound,
            saveOwnersAndDirectors: action.bound,
            saveStoresData: action.bound,
            validateOwnersAndDirectorsOnly: action.bound,
            saveBankDetails: action.bound,
            saveStores: action.bound,
            deleteStore: action.bound,
            submitApplication: action.bound,
            loadOnboardingData: action.bound,
            loadSelfOnboardingApplicationData: action.bound,
            updateStepCompletionStatus: action.bound,
            checkCompanyDetailsComplete: action.bound,
            checkCompanyContactsComplete: action.bound,
            checkOwnersDirectorsComplete: action.bound,
            checkBankDetailsComplete: action.bound,
            checkOutletsComplete: action.bound,
            getStepValidationDetails: action.bound,
            checkSelfOnboardingProcessStatus: action.bound,
            initializeSelfOnboardingProcess: action.bound,
            startSelfOnboardingApplication: action.bound,

            searchCompaniesByName: action.bound,
            getCompanyInfo: action.bound,
            searchAddressByPostalCode: action.bound,
            getDictionary: action.bound,
            verifyPerson: action.bound,
            validateApplication: action.bound,
            reloadApplicationData: action.bound,
            markOwnerFormChanged: action.bound,
            markDirectorFormChanged: action.bound,
            resetFormChangeTracking: action.bound,
            resetApplication: action.bound,
            loadBusinessStructures: action.bound,
            mapCompanyType: action.bound,
            formatDateOfCreation: action.bound,
            isValidDateForForm: action.bound,
            handleCompanyDetailsFormSave: action.bound,
            handleEntityTypeReset: action.bound,
            handleCompanySelectionReset: action.bound,
            shouldShowCompanySelectionConfirmation: action.bound,
            isCompanyDetailsFormValid: action.bound,
            getLegalBusinessNameInfoText: action.bound,
            requiresCompanyNumber: action.bound,
            requiresRegistrationAddress: action.bound,
            requiresCharityFields: action.bound,
            syncRawApplicationDataWithBankAccounts: action.bound,
            prefillCompanyInfoIfNeeded: action.bound,
            getAccountLabelByNumber: action.bound
        })

        this.initialize()

        reaction(
            () => [
                this.companyDetails,
                this.companyContacts,
                this.owners.length,
                this.directors.length,
                this.corporateOwners.length,
                this.bankDetails,
                this.stores.length,
                // Also track changes to individual items
                JSON.stringify(this.owners.map((o) => ({isComplete: o.isComplete, id: o.id}))),
                JSON.stringify(this.directors.map((d) => ({isComplete: d.isComplete, id: d.id}))),
                JSON.stringify(
                    this.corporateOwners.map((c) => ({isComplete: c.isComplete, id: c.id}))
                ),
                JSON.stringify(
                    this.bankDetails?.accounts?.map((a) => ({isComplete: a.isComplete, id: a.id}))
                ),
                JSON.stringify(this.stores.map((s) => ({id: s.id})))
            ],
            () => this.updateStepCompletionStatus(),
            {fireImmediately: true}
        )

        reaction(
            () => this.processKey,
            (processKey, prevProcessKey) => {
                if (processKey && !prevProcessKey && !this.isInitialized) {
                    this.initializeSelfOnboardingProcess()
                }
            },
            {fireImmediately: false}
        )
    }

    get originalCompanyInfo() {
        if (!this._originalCompanyInfo) {
            return null
        }

        return this._originalCompanyInfo
    }

    private async initialize(): Promise<void> {
        try {
            if (this.processKey && !this.isInitialized) {
                await this.initializeSelfOnboardingProcess()
            }
        } catch (error) {
            captureSentryError({
                name: 'OnboardingStore initialize Error',
                description: getErrorMessage(error, 'Failed to initialize onboarding'),
                fingerprint: ['onboarding-store-initialize'],
                error
            })
        }
    }

    goToStep(step: OnboardingStep): void {
        this.currentStep = step
        // Reset the flag when programmatically navigating (like Continue buttons)
        // This allows future data loads to auto-determine the step
        this.hasExplicitStepFromNavigation = false

        const routerStore = getRouterStore()
        const stepRouteMap = {
            [OnboardingStep.START]: ROUTES.onboarding.base,
            [OnboardingStep.COMPANY_DETAILS]: ROUTES.onboarding.companyDetails,
            [OnboardingStep.COMPANY_CONTACTS]: ROUTES.onboarding.companyContacts,
            [OnboardingStep.OWNERS_DIRECTORS]: ROUTES.onboarding.ownersDirectors,
            [OnboardingStep.BANK_DETAILS]: ROUTES.onboarding.bankDetails,
            [OnboardingStep.OUTLETS_INFORMATION]: ROUTES.onboarding.outletsInformation,
            [OnboardingStep.REVIEW_SUBMIT]: ROUTES.onboarding.reviewSubmit,
            [OnboardingStep.APPLICATION_PROGRESS]: ROUTES.onboarding.applicationProgress
        }

        const route = stepRouteMap[step]
        if (route) {
            routerStore.push(route)
        }
    }

    // Method for URL-based navigation (when user navigates directly via URL)
    setStepFromNavigation(step: OnboardingStep): void {
        this.currentStep = step
        this.hasExplicitStepFromNavigation = true
    }

    goToNextStep(): void {
        if (!this.canProceedToNextStep) {
            return
        }

        switch (this.currentStep) {
            case OnboardingStep.START:
                this.stepCompletionStatus[OnboardingStep.START] = true
                this.goToStep(OnboardingStep.COMPANY_DETAILS)
                break
            case OnboardingStep.COMPANY_DETAILS:
                this.goToStep(OnboardingStep.COMPANY_CONTACTS)
                break
            case OnboardingStep.COMPANY_CONTACTS:
                this.goToStep(OnboardingStep.OWNERS_DIRECTORS)
                break
            case OnboardingStep.OWNERS_DIRECTORS:
                this.goToStep(OnboardingStep.BANK_DETAILS)
                break
            case OnboardingStep.BANK_DETAILS:
                this.goToStep(OnboardingStep.OUTLETS_INFORMATION)
                break
            case OnboardingStep.OUTLETS_INFORMATION:
                this.goToStep(OnboardingStep.REVIEW_SUBMIT)
                break
            case OnboardingStep.REVIEW_SUBMIT:
                this.goToStep(OnboardingStep.APPLICATION_PROGRESS)
                break
            default:
                break
        }
    }

    goToPreviousStep(): void {
        switch (this.currentStep) {
            case OnboardingStep.COMPANY_CONTACTS:
                this.goToStep(OnboardingStep.COMPANY_DETAILS)
                break
            case OnboardingStep.OWNERS_DIRECTORS:
                this.goToStep(OnboardingStep.COMPANY_CONTACTS)
                break
            case OnboardingStep.BANK_DETAILS:
                this.goToStep(OnboardingStep.OWNERS_DIRECTORS)
                break
            case OnboardingStep.OUTLETS_INFORMATION:
                this.goToStep(OnboardingStep.BANK_DETAILS)
                break
            case OnboardingStep.REVIEW_SUBMIT:
                this.goToStep(OnboardingStep.OUTLETS_INFORMATION)
                break
            default:
                break
        }
    }

    async terminateProcess(): Promise<void> {
        try {
            if (!this.processKey) {
                throw new Error('Process key is required to terminate process')
            }

            await terminateSelfOnboardingProcess(this.processKey)

            this.resetApplication()

            this.skipFetchingExistingData = false
        } catch (error: any) {
            captureSentryError({
                name: 'OnboardingStore terminateProcess Error',
                description: getErrorMessage(error, 'Failed to terminate process'),
                fingerprint: ['onboarding-store-terminate-process'],
                error
            })
            throw error
        }
    }

    shouldShowTerminationConfirmation(
        newData: CompanyDetailsFormData,
        originalCompanyDetails: any
    ): boolean {
        const hasEntityTypeChangeToLimited =
            originalCompanyDetails &&
            originalCompanyDetails.entityType !== newData.entityType &&
            newData.entityType === BusinessStructure.COMPANY

        const hasExistingData = !!(
            this.companyContacts ||
            (this.owners && this.owners.length > 0) ||
            (this.directors && this.directors.length > 0) ||
            this.bankDetails ||
            (this.stores && this.stores.length > 0)
        )

        const hasCompanyDetailsChanged =
            originalCompanyDetails &&
            (originalCompanyDetails.entityType !== newData.entityType ||
                originalCompanyDetails.companyName !== newData.companyName ||
                originalCompanyDetails.companyTradeName !== newData.companyTradeName ||
                originalCompanyDetails.companyNumber !== newData.companyNumber)

        return (
            (hasEntityTypeChangeToLimited || (hasCompanyDetailsChanged && hasExistingData)) &&
            hasExistingData
        )
    }

    async saveCompanyDetailsWithTerminationCheck(
        data: CompanyDetailsFormData,
        originalCompanyDetails: any,
        onShowConfirmation: () => Promise<boolean>
    ): Promise<void> {
        try {
            this.isSubmitting = true

            if (!this.processKey) {
                throw new Error('Process key is required to save company details')
            }

            if (this.shouldShowTerminationConfirmation(data, originalCompanyDetails)) {
                this.isSubmitting = false
                const confirmed = await onShowConfirmation()
                if (!confirmed) {
                    return
                }

                this.isSubmitting = true
                await this.terminateProcess()
                await new Promise((resolve) => setTimeout(resolve, 5000))
            }

            await this.saveCompanyDetails(data)
        } catch (error: any) {
            throw error
        } finally {
            this.isSubmitting = false
        }
    }

    async saveCompanyDetails(data: CompanyDetailsFormData): Promise<void> {
        try {
            this.isSubmitting = true

            if (!this.processKey) {
                throw new Error('Process key is required to save company details')
            }

            const authStore = subscribe<AuthStoreInterface>(AuthStoreSymbol)
            const userEmail = getCurrentUserEmail(authStore as any)

            const statusResponse = await fetchSelfOnboardingProcessStatus(this.processKey)
            let activeProcessFound = false

            if (statusResponse?.result) {
                const response = statusResponse.result as OnboardingProcessStatusResponse
                activeProcessFound = response.activeProcessFound
            }

            if (!activeProcessFound) {
                const startApplicationData = mapCompanyDetailsToStartApplication(data, userEmail)
                const result = await startSelfOnboardingApplication(
                    this.processKey,
                    startApplicationData
                )

                if (!result) {
                    throw new Error('No result returned from start application API')
                }
            } else {
                let existingData = null
                if (!this.skipFetchingExistingData) {
                    existingData = await fetchSelfOnboardingApplicationDetails(this.processKey)
                }

                const saveApplicationData = mapFormDataToSaveApplication(
                    data,
                    this.companyContacts,
                    existingData
                )

                await saveSelfOnboardingApplication(this.processKey, saveApplicationData)
            }

            this.companyDetails = data

            this.updateStepCompletionStatus()

            this.goToNextStep()
        } catch (error: unknown) {
            captureSentryError({
                name: 'OnboardingStore saveCompanyDetails Error',
                description: getErrorMessage(error, 'Failed to save company details'),
                fingerprint: ['onboarding-store-save-company-details'],
                error
            })
            throw error
        } finally {
            this.isSubmitting = false
        }
    }

    async saveCompanyContacts(data: CompanyContactsFormData): Promise<void> {
        console.log('data company details', data)
        try {
            this.isSubmitting = true

            if (!this.processKey) {
                throw new Error('Process key is required to save contacts')
            }

            const authStore = subscribe<AuthStoreInterface>(AuthStoreSymbol)
            const userEmail = getCurrentUserEmail(authStore as any)

            let existingData = null
            if (!this.skipFetchingExistingData) {
                existingData = await fetchSelfOnboardingApplicationDetails(this.processKey)
            }

            const saveApplicationData = mapFormDataToSaveApplication(
                this.companyDetails,
                data,
                existingData
            )

            const charityNumber =
                this.rawApplicationData?.companyProfile?.charityNumber ||
                this.companyDetails?.charityNumber
            const charityCommission =
                this.rawApplicationData?.companyProfile?.charityCommission ||
                this.companyDetails?.charityCommission

            const validationData: any = {
                existingClient: this.originalCompanyInfo?.existingClient,
                uberId: this.originalCompanyInfo?.uberId,
                businessStructure: this.companyDetails?.entityType || BusinessStructure.COMPANY,
                paymentSolution: '',
                isFastTrack: false,
                isMsaSigned: false,
                processSettings: {
                    bankDetailScheme: 'UK'
                },
                companyProfile: {
                    companyAddress: {
                        postalCode: data.address?.postalCode || '',
                        addressLine1: data.address?.addressLine1 || '',
                        addressLine2: data.address?.addressLine2 || '',
                        townOrCity: data.address?.townOrCity || '',
                        countyOrState: ''
                    },
                    numberOfEmployees: this.companyDetails?.numberOfEmployees || '2',
                    tradeOutsideEea: false,
                    companyName: this.companyDetails?.companyName || '',
                    companyTradeName: this.companyDetails?.companyTradeName || '',
                    companyNumber: this.companyDetails?.companyNumber || '',
                    dateOfCreation: this.companyDetails?.dateOfCreation || '',
                    emailAddress: data.emailAddress || '',
                    telephoneNumber: data.telephoneNumber || '',
                    companyType: this.companyDetails?.entityType || BusinessStructure.COMPANY,
                    ...(charityNumber ? {charityNumber} : {}),
                    ...(charityCommission ? {charityCommission} : {})
                },
                version: '2.1',
                applicantInfo: {
                    initiatorEmail: userEmail
                },
                productVersion: '2'
            }

            const validationResult = await validateSelfOnboardingApplication(validationData)
            this.updateStepCompletionStatus()

            if (validationResult.startApplication && !validationResult.startApplication.complete) {
                const errors = validationResult.startApplication.errors || []
                const description = validationResult.startApplication.description

                if (errors.length > 0) {
                    const errorMessage = errors.join(', ')
                    throw new Error(errorMessage)
                } else {
                    throw new Error(description)
                }
            }

            await saveSelfOnboardingApplication(this.processKey, saveApplicationData)

            this.companyContacts = data

            this.validationResponse = await this.validateApplication()
            this.updateStepCompletionStatus()

            this.goToNextStep()
        } catch (error: unknown) {
            captureSentryError({
                name: 'OnboardingStore saveCompanyContacts Error',
                description: getErrorMessage(error, 'Failed to save company contacts'),
                fingerprint: ['onboarding-store-save-company-contacts'],
                error
            })
            throw error
        } finally {
            this.isSubmitting = false
        }
    }

    markOwnerFormChanged(ownerId: string): void {
        this.ownersFormChanged.set(ownerId, true)
    }

    markDirectorFormChanged(directorId: string): void {
        this.directorsFormChanged.set(directorId, true)
    }

    resetFormChangeTracking(): void {
        this.ownersFormChanged.clear()
        this.directorsFormChanged.clear()
    }

    resetApplication(): void {
        this.companyContacts = undefined
        this.owners = []
        this.directors = []
        this.bankDetails = undefined
        this.stores = []
        this.validationResponse = undefined
        this.rawApplicationData = undefined
        this.documentUploadProgress.clear()
        this.uploadedDocuments.clear()
        this.resetFormChangeTracking()

        this.skipFetchingExistingData = true

        this.stepCompletionStatus = {
            [OnboardingStep.START]: false,
            [OnboardingStep.COMPANY_DETAILS]: false,
            [OnboardingStep.COMPANY_CONTACTS]: false,
            [OnboardingStep.OWNERS_DIRECTORS]: false,
            [OnboardingStep.BANK_DETAILS]: false,
            [OnboardingStep.OUTLETS_INFORMATION]: false,
            [OnboardingStep.REVIEW_SUBMIT]: false,
            [OnboardingStep.APPLICATION_PROGRESS]: false
        }

        this.currentStep = OnboardingStep.COMPANY_DETAILS
    }

    async saveOwner(data: OwnerData): Promise<void> {
        try {
            this.isSubmitting = true

            if (!this.processKey) {
                throw new Error('Process key is required to save owner')
            }

            if (data.id) {
                this.markOwnerFormChanged(data.id)
            }

            if (data.id && this.owners.some((owner) => owner.id === data.id)) {
                this.owners = this.owners.map((owner) =>
                    owner.id === data.id
                        ? {
                              ...owner,
                              ...data,
                              isComplete:
                                  data.verificationResponse?.isVerified === true ||
                                  data.isVerified === true ||
                                  data.uploadDocManually === true ||
                                  (data.documents && data.documents.length > 0)
                          }
                        : owner
                )
            } else {
                const newOwner = {
                    ...data,
                    id: data.id || uuid(),
                    isComplete:
                        data.verificationResponse?.isVerified === true ||
                        data.isVerified === true ||
                        data.uploadDocManually === true ||
                        (data.documents && data.documents.length > 0)
                }
                this.owners.push(newOwner)
            }

            if (this.isPartnershipFlow && this.owners.length > 0) {
                this.ensurePartnerSignatory()
            }

            const saveResult = await this.saveOwnersAndDirectors()

            if (!saveResult.success) {
                const errorMessage = saveResult.errors?.join(', ') || 'Failed to save owner'

                console.log(errorMessage)
            }

            await this.reloadApplicationData('save owner')

            this.validationResponse = await this.validateApplication()

            this.updateStepCompletionStatus()
        } catch (error) {
            captureSentryError({
                name: 'OnboardingStore saveOwner Error',
                description: getErrorMessage(error, 'Failed to save owner'),
                fingerprint: ['onboarding-store-save-owner'],
                error
            })
            throw error
        } finally {
            this.isSubmitting = false
        }
    }

    async updateOwner(id: string, data: Partial<OwnerData>): Promise<void> {
        try {
            this.isSubmitting = true

            if (!this.processKey) {
                throw new Error('Process key is required to update owner')
            }

            this.owners = this.owners.map((owner) =>
                owner.id === id
                    ? {
                          ...owner,
                          ...data,
                          isComplete:
                              data.verificationResponse?.isVerified === true ||
                              data.isVerified === true,
                          isVerified:
                              data.isVerified === true ||
                              data.verificationResponse?.isVerified === true,
                          ...(data.verificationResponse
                              ? {officerVerificationResult: data.verificationResponse}
                              : {})
                      }
                    : owner
            )

            if (this.isPartnershipFlow && this.owners.length > 0) {
                this.ensurePartnerSignatory()
            }

            const saveResult = await this.saveOwnersAndDirectors()

            if (!saveResult.success) {
                const errorMessage = saveResult.errors?.join(', ') || 'Failed to update owner'
                throw new Error(errorMessage)
            }

            await this.reloadApplicationData('update owner')

            this.validationResponse = await this.validateApplication()

            this.updateStepCompletionStatus()
        } catch (error) {
            throw error
        } finally {
            this.isSubmitting = false
        }
    }

    async deleteOwner(id: string): Promise<void> {
        try {
            this.isSubmitting = true

            if (!this.processKey) {
                throw new Error('Process key is required to delete owner')
            }

            const owner = this.owners.find((o) => o.id === id)
            if (owner && owner.deletionAllowed === false) {
                throw new Error('This person cannot be deleted')
            }

            this.owners = this.owners.filter((owner) => owner.id !== id)

            // Clear form change tracking for deleted owner
            this.ownersFormChanged.delete(id)

            const saveResult = await this.saveOwnersAndDirectors()

            if (!saveResult.success) {
                const errorMessage = saveResult.errors?.join(', ') || 'Failed to delete owner'
                throw new Error(errorMessage)
            }

            await this.reloadApplicationData('delete owner')

            // Clear any stale form change tracking after reload
            this.resetFormChangeTracking()

            // Validate application after deleting owner
            this.validationResponse = await this.validateApplication()

            this.updateStepCompletionStatus()
        } catch (error) {
            captureSentryError({
                name: 'OnboardingStore deleteOwner Error',
                description: getErrorMessage(error, 'Failed to delete owner'),
                fingerprint: ['onboarding-store-delete-owner'],
                error
            })
            throw error
        } finally {
            this.isSubmitting = false
        }
    }

    async saveDirector(data: DirectorData): Promise<void> {
        try {
            this.isSubmitting = true

            if (!this.processKey) {
                throw new Error('Process key is required to save director')
            }

            if (data.id) {
                this.markDirectorFormChanged(data.id)
            }

            if (data.id && this.directors.some((director) => director.id === data.id)) {
                this.directors = this.directors.map((director) =>
                    director.id === data.id
                        ? {
                              ...director,
                              ...data,
                              isComplete:
                                  data.verificationResponse?.isVerified === true ||
                                  data.isVerified === true ||
                                  data.uploadDocManually === true ||
                                  (data.documents && data.documents.length > 0)
                          }
                        : director
                )
            } else {
                const newDirector = {
                    ...data,
                    id: data.id || uuid(),
                    isComplete:
                        data.verificationResponse?.isVerified === true ||
                        data.isVerified === true ||
                        data.uploadDocManually === true ||
                        (data.documents && data.documents.length > 0)
                }
                this.directors.push(newDirector)

                this.ensureDirectorSignatory()
            }

            if (this.isPartnershipFlow && this.owners.length > 0) {
                this.ensurePartnerSignatory()
            }

            const saveResult = await this.saveOwnersAndDirectors()

            if (!saveResult.success) {
                const errorMessage = saveResult.errors?.join(', ') || 'Failed to save director'
                throw new Error(errorMessage)
            }

            await this.reloadApplicationData('save director')

            // Validate application after saving director
            this.validationResponse = await this.validateApplication()

            this.updateStepCompletionStatus()
        } catch (error) {
            captureSentryError({
                name: 'OnboardingStore saveDirector Error',
                description: getErrorMessage(error, 'Failed to save director'),
                fingerprint: ['onboarding-store-save-director'],
                error
            })
            throw error
        } finally {
            this.isSubmitting = false
        }
    }

    async updateDirector(id: string, data: Partial<DirectorData>): Promise<void> {
        try {
            this.isSubmitting = true

            if (!this.processKey) {
                throw new Error('Process key is required to update director')
            }

            this.directors = this.directors.map((director) =>
                director.id === id
                    ? {
                          ...director,
                          ...data,
                          isComplete:
                              data.verificationResponse?.isVerified === true ||
                              data.isVerified === true,
                          isVerified:
                              data.isVerified === true ||
                              data.verificationResponse?.isVerified === true,
                          ...(data.verificationResponse
                              ? {officerVerificationResult: data.verificationResponse}
                              : {})
                      }
                    : director
            )

            this.ensureDirectorSignatory()

            if (this.isPartnershipFlow && this.owners.length > 0) {
                this.ensurePartnerSignatory()
            }

            const saveResult = await this.saveOwnersAndDirectors()

            if (!saveResult.success) {
                const errorMessage = saveResult.errors?.join(', ') || 'Failed to update director'
                throw new Error(errorMessage)
            }

            await this.reloadApplicationData('update director')

            // Validate application after updating director
            this.validationResponse = await this.validateApplication()

            this.updateStepCompletionStatus()
        } catch (error) {
            throw error
        } finally {
            this.isSubmitting = false
        }
    }

    async deleteDirector(id: string): Promise<void> {
        try {
            this.isSubmitting = true

            if (!this.processKey) {
                throw new Error('Process key is required to delete director')
            }

            const director = this.directors.find((d) => d.id === id)
            if (director && director.deletionAllowed === false) {
                throw new Error('This person cannot be deleted')
            }

            this.directors = this.directors.filter((director) => director.id !== id)

            // Clear form change tracking for deleted director
            this.directorsFormChanged.delete(id)

            const saveResult = await this.saveOwnersAndDirectors()

            if (!saveResult.success) {
                const errorMessage = saveResult.errors?.join(', ') || 'Failed to delete director'
                throw new Error(errorMessage)
            }

            await this.reloadApplicationData('delete director')

            // Clear any stale form change tracking after reload
            this.resetFormChangeTracking()

            // Validate application after deleting director
            this.validationResponse = await this.validateApplication()

            this.updateStepCompletionStatus()
        } catch (error) {
            throw error
        } finally {
            this.isSubmitting = false
        }
    }

    async updateSignatory(directorId: string, isSignatory: boolean): Promise<void> {
        try {
            this.isSubmitting = true

            if (!this.processKey) {
                throw new Error('Process key is required to update signatory')
            }

            if (isSignatory) {
                this.directors = this.directors.map((director) => ({
                    ...director,
                    agreementSignatory: director.id === directorId
                }))
            } else {
                this.directors = this.directors.map((director) =>
                    director.id === directorId
                        ? {...director, agreementSignatory: isSignatory}
                        : director
                )
            }

            const saveResult = await this.saveOwnersAndDirectors()

            if (!saveResult.success) {
                const errorMessage = saveResult.errors?.join(', ') || 'Failed to update signatory'
                throw new Error(errorMessage)
            }

            await this.reloadApplicationData('update signatory')

            // Validate application after updating signatory
            this.validationResponse = await this.validateApplication()

            this.updateStepCompletionStatus()
        } catch (error) {
            throw error
        } finally {
            this.isSubmitting = false
        }
    }

    async saveOwnersAndDirectors(): Promise<{success: boolean; errors?: string[]}> {
        try {
            this.isSubmitting = true

            if (!this.processKey) {
                throw new Error('Process key is required to save owners and directors')
            }

            const authStore = subscribe<AuthStoreInterface>(AuthStoreSymbol)
            const userEmail = getCurrentUserEmail(authStore as any)

            const entityType = this.companyDetails?.entityType

            const charityNumber =
                this.rawApplicationData?.companyProfile?.charityNumber ||
                this.companyDetails?.charityNumber
            const charityCommission =
                this.rawApplicationData?.companyProfile?.charityCommission ||
                this.companyDetails?.charityCommission
            console.log('this.companyDetails true', charityNumber, charityCommission)

            const applicationData: any = {
                ...this.rawApplicationData,
                businessStructure: entityType,
                companyProfile: {
                    ...this.rawApplicationData?.companyProfile,
                    numberOfEmployees: this.companyDetails?.numberOfEmployees || '2',
                    tradeOutsideEea: false,
                    bankAccountName: this.bankDetails?.accounts?.[0]?.accountName || '',
                    companyAddress: {
                        postalCode: this.companyContacts?.address?.postalCode || '',
                        addressLine1: this.companyContacts?.address?.addressLine1 || '',
                        addressLine2: this.companyContacts?.address?.addressLine2 || '',
                        townOrCity: this.companyContacts?.address?.townOrCity || ''
                    },
                    companyName: this.companyDetails?.companyName || '',
                    companyTradeName: this.companyDetails?.companyTradeName || '',
                    companyNumber: this.companyDetails?.companyNumber || '',
                    dateOfCreation: this.companyDetails?.dateOfCreation || '',
                    emailAddress: this.companyContacts?.emailAddress || '',
                    telephoneNumber: this.companyContacts?.telephoneNumber || '',
                    bankAccountNumber: this.bankDetails?.accounts?.[0]?.bankAccountNumber || '',
                    sortCode: this.bankDetails?.accounts?.[0]?.sortCode || '',
                    accountName: this.bankDetails?.accounts?.[0]?.accountName || '',
                    bankName: this.bankDetails?.accounts?.[0]?.bankName || '',
                    bankAccountType: this.bankDetails?.accounts?.[0]?.bankAccountType || 'business',
                    bankAccountVerificationResponse:
                        this.bankDetails?.accounts?.[0]?.verificationResponse || null,
                    registeredOfficeAddress: null,
                    companyType: this.companyDetails?.entityType || BusinessStructure.COMPANY,
                    directorsPartnersCount: this.directors.length,
                    charityNumber,
                    charityCommission,
                    directDebitAccount: null,
                    directDebitBankDetails: null
                },
                companyDirectors: this.directors.map((director) => {
                    return {
                        id: director.id ? director.id : uuid(),
                        title: director.title,
                        firstName: director.firstName,
                        middleName: director.middleName,
                        surname: director.surname,
                        dateOfBirth: director.dateOfBirth,
                        placeOfBirth: director.placeOfBirth || '',
                        nationality: director.nationality || '',
                        gender: director.gender || '',
                        emailAddress: director.emailAddress,
                        telephoneNumber: director.telephoneNumber,
                        agreementSignatory: director.agreementSignatory,
                        residentialAddress: {
                            country:
                                director.address?.country || director.residentialAddress?.country,
                            postalCode:
                                director.address?.postalCode ||
                                director.residentialAddress?.postalCode ||
                                '',
                            houseNameOrNumber: '',
                            addressLine1:
                                director.address?.addressLine1 ||
                                director.residentialAddress?.addressLine1 ||
                                '',
                            addressLine2:
                                director.address?.addressLine2 ||
                                director.residentialAddress?.addressLine2 ||
                                '',
                            townOrCity:
                                director.address?.townOrCity ||
                                director.residentialAddress?.townOrCity ||
                                ''
                        },
                        deletionAllowed: director.deletionAllowed ?? false,
                        uploadDocManually:
                            director.uploadDocManually ||
                            (director.documents && director.documents.length > 0) ||
                            false,
                        officerVerificationResult:
                            director.officerVerificationResult || director.verificationResponse,
                        isVerified:
                            director.isVerified ||
                            director.verificationResponse?.isVerified ||
                            director.officerVerificationResult?.isVerified ||
                            director.uploadDocManually ||
                            (director.documents && director.documents.length > 0) ||
                            null,
                        documents: director.documents || []
                    }
                }),
                ...(() => {
                    const personOwners = this.owners.filter((owner) => !owner.isCompany)
                    const companyOwners = this.owners.filter((owner) => owner.isCompany)

                    const result: any = {}

                    if (personOwners.length > 0) {
                        if (
                            this.isSoleTraderOrPartnershipFlow ||
                            this.isOtherFlow ||
                            this.isCharityFlow
                        ) {
                            result.businessOwners = personOwners.map((owner) => {
                                return {
                                    ...owner,
                                    id: owner.id ? owner.id : uuid(),
                                    title: owner.title || '',
                                    firstName: owner.firstName || '',
                                    middleName: owner.middleName || '',
                                    surname: owner.lastName || owner.surname || '',
                                    dateOfBirth: owner.dateOfBirth,
                                    placeOfBirth: owner.placeOfBirth || '',
                                    nationality: owner.nationality || '',
                                    gender: owner.gender || '',
                                    emailAddress: owner.emailAddress || '',
                                    telephoneNumber: owner.phone || owner.telephoneNumber || '',
                                    residentialAddress: {
                                        country:
                                            owner.address?.country ||
                                            owner.residentialAddress?.country,
                                        postalCode:
                                            owner.address?.postalCode ||
                                            owner.residentialAddress?.postalCode,
                                        houseNameOrNumber: '',
                                        addressLine1:
                                            owner.address?.addressLine1 ||
                                            owner.residentialAddress?.addressLine1,
                                        addressLine2:
                                            owner.address?.addressLine2 ||
                                            owner.residentialAddress?.addressLine2,
                                        townOrCity:
                                            owner.address?.townOrCity ||
                                            owner.residentialAddress?.townOrCity
                                    },
                                    ownershipPercentage: owner.ownershipPercentage,
                                    deletionAllowed: owner.deletionAllowed ?? false,
                                    uploadDocManually:
                                        owner.uploadDocManually ||
                                        (owner.documents && owner.documents.length > 0) ||
                                        false,
                                    officerVerificationResult:
                                        owner.officerVerificationResult ||
                                        owner.verificationResponse,
                                    isVerified:
                                        owner.isVerified ||
                                        owner.uploadDocManually ||
                                        (owner.documents && owner.documents.length > 0) ||
                                        owner.verificationResponse?.isVerified ||
                                        owner.officerVerificationResult?.isVerified ||
                                        null,
                                    documents: owner.documents || []
                                }
                            })
                        } else {
                            result.individualShareholders = personOwners.map((owner) => {
                                return {
                                    id: owner.id ? owner.id : uuid(),
                                    title: owner.title || '',
                                    firstName: owner.firstName || '',
                                    middleName: owner.middleName || '',
                                    surname: owner.lastName || owner.surname || '',
                                    dateOfBirth: owner.dateOfBirth,
                                    placeOfBirth: owner.placeOfBirth || '',
                                    nationality: owner.nationality || '',
                                    gender: owner.gender || '',
                                    emailAddress: owner.emailAddress || '',
                                    telephoneNumber: owner.phone || owner.telephoneNumber || '',
                                    residentialAddress: {
                                        country:
                                            owner.address?.country ||
                                            owner.residentialAddress?.country,
                                        postalCode:
                                            owner.address?.postalCode ||
                                            owner.residentialAddress?.postalCode,
                                        houseNameOrNumber: '',
                                        addressLine1:
                                            owner.address?.addressLine1 ||
                                            owner.residentialAddress?.addressLine1,
                                        addressLine2:
                                            owner.address?.addressLine2 ||
                                            owner.residentialAddress?.addressLine2,
                                        townOrCity:
                                            owner.address?.townOrCity ||
                                            owner.residentialAddress?.townOrCity ||
                                            ''
                                    },
                                    ownershipPercentage: owner.ownershipPercentage,
                                    deletionAllowed: owner.deletionAllowed ?? false,
                                    uploadDocManually:
                                        owner.uploadDocManually ||
                                        (owner.documents && owner.documents.length > 0) ||
                                        false,
                                    officerVerificationResult:
                                        owner.officerVerificationResult ||
                                        owner.verificationResponse,
                                    isVerified:
                                        owner.uploadDocManually ||
                                        (owner.documents && owner.documents.length > 0) ||
                                        owner.verificationResponse?.isVerified ||
                                        owner.officerVerificationResult?.isVerified ||
                                        null,
                                    documents: owner.documents || []
                                }
                            })
                        }
                    }

                    if (companyOwners.length > 0) {
                        result.corporateEntityShareholders = companyOwners.map((owner) => ({
                            companyName: owner.firstName || '',
                            companyNumber: owner.companyNumber || '',
                            ownershipPercentage: owner.ownershipPercentage || 0,
                            deletionAllowed: owner.deletionAllowed ?? false,
                            uploadDocManually: owner.uploadDocManually || false,
                            officerVerificationResult: owner.verificationResponse || null
                        }))
                    }

                    if (this.corporateOwners.length > 0) {
                        result.corporateEntityShareholders = (
                            result.corporateEntityShareholders || []
                        ).concat(
                            this.corporateOwners.map((corporateOwner) => ({
                                companyName: corporateOwner.companyName || '',
                                companyNumber: corporateOwner.companyNumber || '',
                                ownershipPercentage: corporateOwner.ownershipPercentage || 0,
                                deletionAllowed: corporateOwner.deletionAllowed !== false,
                                uploadDocManually: corporateOwner.uploadDocManually || false,
                                fromApi: corporateOwner.fromApi || false,
                                officerVerificationResult:
                                    corporateOwner.officerVerificationResult || null,
                                agreementSignatory: corporateOwner.agreementSignatory || null,
                                jumioCheckResults: corporateOwner.jumioCheckResults || null,
                                jumioRequestSent: corporateOwner.jumioRequestSent || null,
                                isVerified: corporateOwner.isVerified || false
                            }))
                        )
                    }

                    return result
                })(),
                ...(this.bankDetails?.accounts?.length > 0
                    ? {
                          bankAccounts: this.bankDetails.accounts.map((account) => ({
                              ...account,
                              id: account.id,
                              bankAccountNumber: account.bankAccountNumber,
                              sortCode: account.sortCode,
                              accountName: account.accountName,
                              bankName: account.bankName,
                              bankAccountType: account.bankAccountType || 'business',
                              isDefault: account.isDefault || false,
                              validationResults: account.verificationResponse?.validationResults,
                              creditSafeVerificationResults:
                                  account.verificationResponse?.creditSafeResults,
                              sortCodeFoundInBase: account.verificationResponse?.sortCodeValid,
                              uploadDocManually: account.uploadDocManually || false,
                              isVerified:
                                  account.uploadDocManually ||
                                  account.verificationResponse?.verified ||
                                  false
                          }))
                      }
                    : {}),
                ...(this.stores?.length > 0
                    ? {
                          stores: this.stores.map((outlet, index) => {
                              const settlementAccount = this.bankDetails?.accounts?.find(
                                  (account) =>
                                      account.bankAccountNumber === outlet.settlementsAccount
                              )
                              const directDebitAccount = this.bankDetails?.accounts?.find(
                                  (account) =>
                                      account.bankAccountNumber === outlet.directDebitAccount
                              )

                              const formatBankAccountForValidation = (account: any) => {
                                  if (!account) return null
                                  return {
                                      id: account.id,
                                      bankAccountNumber: account.bankAccountNumber,
                                      sortCode: account.sortCode,
                                      accountName: account.accountName,
                                      bankName: account.bankName,
                                      bankAccountType: account.bankAccountType || 'business',
                                      isDefault: account.isDefault || false,
                                      validationResults:
                                          account.verificationResponse?.validationResults,
                                      creditSafeVerificationResults:
                                          account.verificationResponse?.creditSafeResults,
                                      sortCodeFoundInBase:
                                          account.verificationResponse?.sortCodeValid,
                                      uploadDocManually: account.uploadDocManually || false,
                                      isVerified:
                                          account.uploadDocManually ||
                                          account.verificationResponse?.verified ||
                                          false
                                  }
                              }

                              return {
                                  ...(outlet.id ? {id: outlet.id} : {}),
                                  storeName: outlet.tradingName,
                                  storeAddress: {
                                      postalCode: outlet.address.postcode,
                                      addressLine1: outlet.address.addressLine1,
                                      addressLine2: outlet.address.addressLine2 || '',
                                      locality: '',
                                      townOrCity: outlet.address.townOrCity
                                  },
                                  systemDescriptor: outlet.systemDescriptor,
                                  requireOriginalCountry: false,
                                  bankInfo: formatBankAccountForValidation(settlementAccount),
                                  directDebitBankInfo: formatBankAccountForValidation(
                                      outlet.useDirectDebit ? settlementAccount : directDebitAccount
                                  ),
                                  processingMethods: getProcessingMethodsTemplate().map(
                                      (method) => {
                                          switch (method.name) {
                                              case 'pos':
                                                  return {
                                                      ...method,
                                                      percent: outlet.posPercentage || 0
                                                  }
                                              case 'moto':
                                                  return {
                                                      ...method,
                                                      percent: outlet.motoPercentage || 0
                                                  }
                                              case 'ecom':
                                                  return {
                                                      ...method,
                                                      percent: outlet.paymentAppsPercentage || 0
                                                  }
                                              case 'payByLink':
                                                  return {
                                                      ...method,
                                                      percent: outlet.paymentLinkPercentage || 0
                                                  }
                                              case 'payByApp':
                                                  return {
                                                      ...method,
                                                      percent: outlet.paymentAppsPercentage || 0
                                                  }
                                              case 'virtualTerminal':
                                                  return {
                                                      ...method,
                                                      percent: outlet.virtualTerminalPercentage || 0
                                                  }
                                              default:
                                                  return {...method, percent: 0}
                                          }
                                      }
                                  ),
                                  advancePayments: [],
                                  natureOfBusiness: outlet.productsYouSell
                              }
                          })
                      }
                    : {}),
                applicantInfo: {
                    ...this.rawApplicationData?.applicantInfo,
                    initiatorEmail: userEmail
                }
            }

            const allDocuments: Array<{type: string; name: string; path: string}> = []

            this.directors.forEach((director, index) => {
                if (director.documents && director.documents.length > 0) {
                    allDocuments.push(...director.documents)
                }
            })

            this.owners.forEach((owner, index) => {
                if (owner.documents && owner.documents.length > 0) {
                    allDocuments.push(...owner.documents)
                }
            })

            this.directors.forEach((director) => {
                if (
                    director.verificationResponse?.isVerified === false &&
                    director.documents &&
                    director.documents.length > 0
                ) {
                }
            })

            this.owners.forEach((owner) => {
                if (
                    owner.verificationResponse?.isVerified === false &&
                    owner.documents &&
                    owner.documents.length > 0
                ) {
                }
            })

            applicationData.documents = allDocuments

            await saveSelfOnboardingApplication(this.processKey, applicationData)

            const validationResult = await validateSelfOnboardingApplication(applicationData)
            this.updateStepCompletionStatus()

            const ownersDirectorsValidation = validationResult.ownerAndDirectors
            if (ownersDirectorsValidation && !ownersDirectorsValidation.complete) {
                const errors: string[] = []

                if (ownersDirectorsValidation.description) {
                    errors.push(ownersDirectorsValidation.description)
                }

                if (
                    ownersDirectorsValidation.errors &&
                    ownersDirectorsValidation.errors.length > 0
                ) {
                    errors.push(...ownersDirectorsValidation.errors.map((err) => `• ${err}`))
                }

                runInAction(() => {
                    this.stepCompletionStatus[OnboardingStep.OWNERS_DIRECTORS] = false
                })

                return {
                    success: false,
                    errors:
                        errors.length > 0
                            ? errors
                            : [i18next.t('OnboardingStore.errors.ownersDirectorsValidationFailed')]
                }
            }

            runInAction(() => {
                this.stepCompletionStatus[OnboardingStep.OWNERS_DIRECTORS] = true
            })

            return {
                success: true
            }
        } catch (error: any) {
            return {
                success: false,
                errors: [error.message || 'Failed to save owners and directors']
            }
        } finally {
            this.isSubmitting = false
        }
    }

    async saveStoresData(storesData?: StoreData[]): Promise<{success: boolean; errors?: string[]}> {
        try {
            this.isSubmitting = true

            if (!this.processKey) {
                throw new Error('Process key is required to save stores')
            }

            const authStore = subscribe<AuthStoreInterface>(AuthStoreSymbol)
            const userEmail = getCurrentUserEmail(authStore as any)
            const entityType = this.companyDetails?.entityType
            const stores = storesData || this.stores

            const charityNumber =
                this.rawApplicationData?.companyProfile?.charityNumber ||
                this.companyDetails?.charityNumber
            const charityCommission =
                this.rawApplicationData?.companyProfile?.charityCommission ||
                this.companyDetails?.charityCommission
            console.log('this.companyDetails true', charityNumber, charityCommission)

            const applicationData: any = {
                businessStructure: entityType,
                paymentSolution: '',
                isFastTrack: false,
                isMsaSigned: false,
                companyProfile: {
                    numberOfEmployees: this.companyDetails?.numberOfEmployees || '2',
                    tradeOutsideEea: false,
                    bankAccountName: this.bankDetails?.accounts?.[0]?.accountName || '',
                    companyAddress: {
                        postalCode: this.companyContacts?.address?.postalCode || '',
                        addressLine1: this.companyContacts?.address?.addressLine1 || '',
                        addressLine2: this.companyContacts?.address?.addressLine2 || '',
                        townOrCity: this.companyContacts?.address?.townOrCity || ''
                    },
                    companyName: this.companyDetails?.companyName || '',
                    companyTradeName: this.companyDetails?.companyTradeName || '',
                    companyNumber: this.companyDetails?.companyNumber || '',
                    dateOfCreation: this.companyDetails?.dateOfCreation || '',
                    emailAddress: this.companyContacts?.emailAddress || '',
                    telephoneNumber: this.companyContacts?.telephoneNumber || '',
                    bankAccountNumber: this.bankDetails?.accounts?.[0]?.bankAccountNumber || '',
                    sortCode: this.bankDetails?.accounts?.[0]?.sortCode || '',
                    accountName: this.bankDetails?.accounts?.[0]?.accountName || '',
                    bankName: this.bankDetails?.accounts?.[0]?.bankName || '',
                    bankAccountType: this.bankDetails?.accounts?.[0]?.bankAccountType || 'business',
                    bankAccountVerificationResponse:
                        this.bankDetails?.accounts?.[0]?.verificationResponse || null,
                    registeredOfficeAddress: null,
                    companyType: this.companyDetails?.entityType || BusinessStructure.COMPANY,
                    directorsPartnersCount: this.directors.length,
                    charityNumber,
                    charityCommission,
                    directDebitAccount: null,
                    directDebitBankDetails: null
                },
                companyDirectors: this.directors.map((director) => ({
                    id: director.id || uuid(),
                    title: director.title,
                    firstName: director.firstName,
                    middleName: director.middleName,
                    surname: director.surname,
                    dateOfBirth: director.dateOfBirth,
                    placeOfBirth: director.placeOfBirth || '',
                    nationality: director.nationality || '',
                    gender: director.gender || '',
                    emailAddress: director.emailAddress,
                    telephoneNumber: director.telephoneNumber,
                    agreementSignatory: director.agreementSignatory,
                    residentialAddress: {
                        country: director.address?.country || director.residentialAddress?.country,
                        postalCode:
                            director.address?.postalCode ||
                            director.residentialAddress?.postalCode ||
                            '',
                        houseNameOrNumber: '',
                        addressLine1:
                            director.address?.addressLine1 ||
                            director.residentialAddress?.addressLine1 ||
                            '',
                        addressLine2:
                            director.address?.addressLine2 ||
                            director.residentialAddress?.addressLine2 ||
                            '',
                        townOrCity:
                            director.address?.townOrCity ||
                            director.residentialAddress?.townOrCity ||
                            ''
                    },
                    deletionAllowed: director.deletionAllowed ?? false,
                    uploadDocManually:
                        director.uploadDocManually ||
                        (director.documents && director.documents.length > 0) ||
                        false,
                    isVerified:
                        director.isVerified ||
                        director.verificationResponse?.isVerified ||
                        director.officerVerificationResult?.isVerified ||
                        director.uploadDocManually ||
                        (director.documents && director.documents.length > 0) ||
                        null,
                    documents: director.documents || [],
                    officerVerificationResult:
                        director.officerVerificationResult || director.verificationResponse
                })),
                version: '2.1',
                applicantInfo: {
                    initiatorEmail: userEmail
                },
                productVersion: '2'
            }

            const personOwners = this.owners.filter((owner) => !owner.isCompany)
            if (personOwners.length > 0) {
                if (this.isSoleTraderOrPartnershipFlow) {
                    applicationData.businessOwners = personOwners.map((owner) => ({
                        ...owner,
                        title: owner.title || '',
                        firstName: owner.firstName || '',
                        middleName: owner.middleName || '',
                        surname: owner.lastName || owner.surname || '',
                        dateOfBirth: owner.dateOfBirth,
                        placeOfBirth: owner.placeOfBirth || '',
                        nationality: owner.nationality || '',
                        gender: owner.gender || '',
                        emailAddress: owner.emailAddress || '',
                        telephoneNumber: owner.phone || owner.telephoneNumber || '',
                        residentialAddress: {
                            country: owner.address?.country || owner.residentialAddress?.country,
                            postalCode:
                                owner.address?.postalCode || owner.residentialAddress?.postalCode,
                            houseNameOrNumber: '',
                            addressLine1:
                                owner.address?.addressLine1 ||
                                owner.residentialAddress?.addressLine1,
                            addressLine2:
                                owner.address?.addressLine2 ||
                                owner.residentialAddress?.addressLine2,
                            townOrCity:
                                owner.address?.townOrCity || owner.residentialAddress?.townOrCity
                        },
                        ownershipPercentage: owner.ownershipPercentage,
                        deletionAllowed: owner.deletionAllowed ?? false,
                        uploadDocManually:
                            owner.uploadDocManually ||
                            (owner.documents && owner.documents.length > 0) ||
                            false,
                        isVerified:
                            owner.isVerified ||
                            owner.uploadDocManually ||
                            (owner.documents && owner.documents.length > 0) ||
                            owner.verificationResponse?.isVerified ||
                            owner.officerVerificationResult?.isVerified ||
                            null,
                        documents: owner.documents || [],
                        officerVerificationResult:
                            owner.officerVerificationResult || owner.verificationResponse
                    }))
                } else {
                    applicationData.individualShareholders = personOwners.map((owner) => ({
                        id: owner.id || uuid(),
                        title: owner.title || '',
                        firstName: owner.firstName || '',
                        middleName: owner.middleName || '',
                        surname: owner.lastName || owner.surname || '',
                        dateOfBirth: owner.dateOfBirth,
                        placeOfBirth: owner.placeOfBirth || '',
                        nationality: owner.nationality || '',
                        gender: owner.gender || '',
                        emailAddress: owner.emailAddress || '',
                        telephoneNumber: owner.phone || owner.telephoneNumber || '',
                        residentialAddress: {
                            country: owner.address?.country || owner.residentialAddress?.country,
                            postalCode:
                                owner.address?.postalCode || owner.residentialAddress?.postalCode,
                            houseNameOrNumber: '',
                            addressLine1:
                                owner.address?.addressLine1 ||
                                owner.residentialAddress?.addressLine1,
                            addressLine2:
                                owner.address?.addressLine2 ||
                                owner.residentialAddress?.addressLine2,
                            townOrCity:
                                owner.address?.townOrCity || owner.residentialAddress?.townOrCity
                        },
                        ownershipPercentage: owner.ownershipPercentage,
                        deletionAllowed: owner.deletionAllowed ?? false,
                        uploadDocManually:
                            owner.uploadDocManually ||
                            (owner.documents && owner.documents.length > 0) ||
                            false,
                        isVerified:
                            owner.isVerified ||
                            owner.uploadDocManually ||
                            (owner.documents && owner.documents.length > 0) ||
                            owner.verificationResponse?.isVerified ||
                            owner.officerVerificationResult?.isVerified ||
                            null,
                        documents: owner.documents || [],
                        officerVerificationResult:
                            owner.officerVerificationResult || owner.verificationResponse
                    }))
                }
            }

            if (this.corporateOwners.length > 0) {
                applicationData.corporateShareholders = this.corporateOwners.map((owner) => ({
                    companyName: owner.companyName,
                    companyNumber: owner.companyNumber,
                    ownershipPercentage: owner.ownershipPercentage,
                    deletionAllowed: owner.deletionAllowed ?? false
                }))
            }

            const allDocuments: any[] = []
            this.owners.forEach((owner) => {
                if (owner.documents && owner.documents.length > 0) {
                    owner.documents.forEach((doc) => {
                        allDocuments.push({
                            ...doc,
                            ownerId: owner.id,
                            ownerType: 'owner'
                        })
                    })
                }
            })

            this.directors.forEach((director) => {
                if (director.documents && director.documents.length > 0) {
                    director.documents.forEach((doc) => {
                        allDocuments.push({
                            ...doc,
                            ownerId: director.id,
                            ownerType: 'director'
                        })
                    })
                }
            })

            applicationData.documents = allDocuments

            if (this.bankDetails?.accounts && this.bankDetails.accounts.length > 0) {
                applicationData.bankAccounts = this.bankDetails.accounts.map((account) => ({
                    ...account,
                    bankName: account.bankName,
                    bankLogo: account.bankLogo,
                    bankAccountNumber: account.bankAccountNumber,
                    sortCode: account.sortCode,
                    accountName: account.accountName,
                    bankAccountType: account.bankAccountType,
                    isComplete: account.isComplete,
                    isVerified: account.isVerified,
                    isDefault: account.isDefault,
                    uploadDocManually: account.uploadDocManually,
                    verificationResponse: account.verificationResponse,
                    validationResults: account.validationResults,
                    creditSafeVerificationResults: account.creditSafeVerificationResults,
                    openBankVerificationResult: account.openBankVerificationResult,
                    openBankingRequestSent: account.openBankingRequestSent,
                    openBankingQuestionnaire: account.openBankingQuestionnaire,
                    sortCodeFoundInBase: account.sortCodeFoundInBase,
                    fromApi: account.fromApi,
                    id: null
                }))
            }

            if (stores.length > 0) {
                applicationData.stores = stores.map((store) => {
                    const settlementsBankAccount = this.bankDetails?.accounts?.find(
                        (account) => account.bankAccountNumber === store.settlementsAccount
                    )
                    const directDebitBankAccount = store.useDirectDebit
                        ? settlementsBankAccount
                        : this.bankDetails?.accounts?.find(
                              (account) => account.bankAccountNumber === store.directDebitAccount
                          )

                    return {
                        storeName: store.tradingName,
                        storeAddress: {
                            addressLine1: store.address.addressLine1,
                            addressLine2: store.address.addressLine2,
                            locality: '',
                            postalCode: store.address.postalCode,
                            townOrCity: store.address.townOrCity,
                            county: '',
                            country: store.address.country
                        },
                        address: {
                            addressLine1: store.address.addressLine1,
                            addressLine2: store.address.addressLine2,
                            townOrCity: store.address.townOrCity,
                            postalCode: store.address.postalCode,
                            postcode: store.address.postalCode,
                            country: store.address.country || 'United Kingdom'
                        },
                        systemDescriptor: store.systemDescriptor,
                        requireOriginalCountry: false,
                        settlementsAccount: store.settlementsAccount,
                        useDirectDebit: store.useDirectDebit,
                        directDebitAccount: store.useDirectDebit
                            ? store.settlementsAccount
                            : store.directDebitAccount,
                        bankInfo: settlementsBankAccount
                            ? {
                                  bankAccountNumber: settlementsBankAccount.bankAccountNumber,
                                  sortCode: settlementsBankAccount.sortCode,
                                  accountName: settlementsBankAccount.accountName,
                                  bankAccountType:
                                      settlementsBankAccount.bankAccountType || 'business',
                                  bankName: settlementsBankAccount.bankName,
                                  bankAccountVerificationResponse:
                                      settlementsBankAccount.verificationResponse
                              }
                            : null,
                        directDebitBankInfo: directDebitBankAccount
                            ? {
                                  bankAccountNumber: directDebitBankAccount.bankAccountNumber,
                                  sortCode: directDebitBankAccount.sortCode,
                                  accountName: directDebitBankAccount.accountName,
                                  bankAccountType:
                                      directDebitBankAccount.bankAccountType || 'business',
                                  bankName: directDebitBankAccount.bankName,
                                  bankAccountVerificationResponse:
                                      directDebitBankAccount.verificationResponse
                              }
                            : null,
                        processingMethods: getProcessingMethodsTemplate().map((method) => {
                            switch (method.name) {
                                case 'pos':
                                    return {...method, percent: store.posPercentage || 0}
                                case 'moto':
                                    return {...method, percent: store.motoPercentage || 0}
                                case 'ecom':
                                    return {...method, percent: 0}
                                case 'payByLink':
                                    return {...method, percent: store.paymentLinkPercentage || 0}
                                case 'payByApp':
                                    return {...method, percent: store.paymentAppsPercentage || 0}
                                case 'virtualTerminal':
                                    return {
                                        ...method,
                                        percent: store.virtualTerminalPercentage || 0
                                    }
                                default:
                                    return {...method, percent: 0}
                            }
                        }),
                        advancePayments: [],
                        natureOfBusiness: store.productsYouSell || ''
                    }
                })
            } else {
                applicationData.stores = []
            }

            await saveSelfOnboardingApplication(this.processKey, applicationData)
            const validationResult = await validateSelfOnboardingApplication(applicationData)
            this.updateStepCompletionStatus()

            const outletsValidation = validationResult.outletInformation
            if (outletsValidation && !outletsValidation.complete) {
                const errors: string[] = []
                if (
                    outletsValidation.errors &&
                    Array.isArray(outletsValidation.errors) &&
                    outletsValidation.errors.length > 0
                ) {
                    errors.push(...outletsValidation.errors.map((err) => `• ${err}`))
                }

                runInAction(() => {
                    this.stepCompletionStatus[OnboardingStep.OUTLETS_INFORMATION] = false
                })

                return {
                    success: false,
                    errors: errors.length > 0 ? errors : ['Stores validation failed']
                }
            }

            runInAction(() => {
                this.stepCompletionStatus[OnboardingStep.OUTLETS_INFORMATION] = true
            })

            return {success: true}
        } catch (error: any) {
            return {
                success: false,
                errors: [error.message || 'Failed to save stores']
            }
        } finally {
            this.isSubmitting = false
        }
    }

    async validateOwnersAndDirectorsOnly(): Promise<{success: boolean; errors?: string[]}> {
        try {
            this.isSubmitting = true

            if (!this.processKey || !this.rawApplicationData) {
                return {
                    success: false,
                    errors: ['No application data available for validation']
                }
            }

            const validationResult = await validateSelfOnboardingApplication(
                this.rawApplicationData
            )

            this.updateStepCompletionStatus()

            const ownersDirectorsValidation = validationResult.ownerAndDirectors
            if (ownersDirectorsValidation && !ownersDirectorsValidation.complete) {
                const errors: string[] = []

                if (ownersDirectorsValidation.description) {
                    errors.push(ownersDirectorsValidation.description)
                }

                if (
                    ownersDirectorsValidation.errors &&
                    ownersDirectorsValidation.errors.length > 0
                ) {
                    errors.push(...ownersDirectorsValidation.errors.map((err) => `• ${err}`))
                }

                return {
                    success: false,
                    errors:
                        errors.length > 0
                            ? errors
                            : [i18next.t('OnboardingStore.errors.ownersDirectorsValidationFailed')]
                }
            }

            return {
                success: true
            }
        } catch (error: any) {
            return {
                success: false,
                errors: [error.message || 'Failed to validate owners and directors']
            }
        } finally {
            this.isSubmitting = false
        }
    }

    async saveBankDetails(data: BankDetailsFormData): Promise<void> {
        try {
            this.isSubmitting = true

            if (!this.processKey) {
                throw new Error('Process key is required to save bank details')
            }

            this.bankDetails = data

            this.syncRawApplicationDataWithBankAccounts()

            const result = await this.saveOwnersAndDirectors()

            if (!result.success) {
                throw new Error(result.errors?.join(', ') || 'Failed to save bank details')
            }

            this.bankAccountStore.resetStore()

            // Update step completion status after saving bank details
            this.updateStepCompletionStatus()
        } catch (error) {
            throw error
        } finally {
            this.isSubmitting = false
        }
    }

    async saveStores(data: StoreData[]): Promise<void> {
        try {
            this.isSubmitting = true
            if (!this.processKey) {
                throw new Error('Process key is required to save stores')
            }

            this.stores = data

            const result = await this.saveStoresData(data)

            if (!result.success) {
                throw new Error(result.errors?.join(', ') || 'Failed to save stores')
            }
        } catch (error) {
            throw error
        } finally {
            this.isSubmitting = false
        }
    }

    async deleteStore(storeId: string | number): Promise<void> {
        try {
            this.isSubmitting = true

            if (!this.processKey) {
                throw new Error('Process key is required to delete store')
            }

            const updatedStores = this.stores.filter((store, index) => {
                if (store.id) {
                    return store.id !== storeId
                }
                return index !== Number(storeId)
            })

            await this.saveStores(updatedStores)

            await this.reloadApplicationData('delete store')
        } catch (error) {
            throw error
        } finally {
            this.isSubmitting = false
        }
    }

    async submitApplication(): Promise<void> {
        try {
            this.isSubmitting = true

            if (!this.isCurrentStepValid) {
                throw new Error('Please complete all required information before submitting')
            }

            if (!this.processKey) {
                throw new Error('Process key is required to submit')
            }

            const updatedApplicationData = await fetchSelfOnboardingApplicationDetails(
                this.processKey
            )

            if (!updatedApplicationData || typeof updatedApplicationData !== 'object') {
                return
            }

            const applicationData = mapFormDataToSaveApplication(
                this.companyDetails,
                this.companyContacts,
                updatedApplicationData
            )

            await submitSelfOnboardingApplication(this.processKey, applicationData)

            this.stepCompletionStatus[OnboardingStep.REVIEW_SUBMIT] = true
        } catch (error) {
            throw error
        } finally {
            this.isSubmitting = false
        }
    }

    get canProceedToNextStep(): boolean {
        return this.isCurrentStepValid
    }

    get isCompanyFlow(): boolean {
        const entityType = this.companyDetails?.entityType
        return entityType === BusinessStructure.COMPANY
    }

    get isLLPFlow(): boolean {
        const entityType = this.companyDetails?.entityType
        return entityType === BusinessStructure.LLP
    }

    get isSoleTraderFlow(): boolean {
        const entityType = this.companyDetails?.entityType
        return entityType === BusinessStructure.SOLE_TRADER
    }

    get isPartnershipFlow(): boolean {
        const entityType = this.companyDetails?.entityType
        return entityType === BusinessStructure.PARTNERSHIP
    }

    get isOtherFlow(): boolean {
        const entityType = this.companyDetails?.entityType
        return entityType === BusinessStructure.OTHER
    }

    get isCharityFlow(): boolean {
        const entityType = this.companyDetails?.entityType
        return entityType === BusinessStructure.CHARITY
    }

    get isCompanyOrLLPFlow(): boolean {
        return this.isCompanyFlow || this.isLLPFlow
    }

    get isSoleTraderOrPartnershipFlow(): boolean {
        return this.isSoleTraderFlow || this.isPartnershipFlow
    }

    getPersonValidationErrors(personId: string): string[] {
        const ownersDirectorsValidation = this.validationResponse?.ownerAndDirectors
        if (!ownersDirectorsValidation?.persons) {
            return []
        }

        const personValidation = ownersDirectorsValidation.persons.find(
            (person: any) => person.id === personId
        )

        return personValidation?.errors || []
    }

    get isCurrentStepValid(): boolean {
        switch (this.currentStep) {
            case OnboardingStep.START:
                return true
            case OnboardingStep.COMPANY_DETAILS:
                return !!this.companyDetails
            case OnboardingStep.COMPANY_CONTACTS:
                return !!this.companyContacts
            case OnboardingStep.OWNERS_DIRECTORS:
                const hasOwners = this.owners.length > 0 || this.corporateOwners.length > 0
                const hasDirectors = this.directors.length > 0
                return hasOwners || hasDirectors
            case OnboardingStep.BANK_DETAILS:
                return !!this.bankDetails && this.bankDetails.accounts.length > 0
            case OnboardingStep.OUTLETS_INFORMATION:
                return !!this.stores && this.stores.length > 0
            case OnboardingStep.REVIEW_SUBMIT:
                for (const [stepKey, isComplete] of Object.entries(this.stepCompletionStatus)) {
                    const step = parseInt(stepKey) as OnboardingStep
                    if (
                        step !== OnboardingStep.START &&
                        step !== OnboardingStep.REVIEW_SUBMIT &&
                        step !== OnboardingStep.APPLICATION_PROGRESS
                    ) {
                        if (!isComplete) {
                            return false
                        }
                    }
                }
                return true
            default:
                return false
        }
    }

    private _profileStore?: ProfileStoreInterface
    private _bankAccountStore?: import('./OnboardingBankAccountStore').OnboardingBankAccountStore

    get processKey(): string | undefined {
        if (!this._profileStore) {
            this._profileStore = subscribe<ProfileStoreInterface>(ProfileStoreSymbol)
        }
        return this._profileStore.profileData?.publicId
    }

    private get bankAccountStore() {
        if (!this._bankAccountStore) {
            this._bankAccountStore = subscribe<OnboardingBankAccountStore>(
                ONBOARDING_STORE_SYMBOLS.BankAccountStore
            )
        }
        return this._bankAccountStore
    }

    updateStepCompletionStatus(): void {
        if (
            this.validationResponse &&
            this.currentStep !== OnboardingStep.COMPANY_DETAILS &&
            this.currentStep !== OnboardingStep.COMPANY_CONTACTS
        ) {
            this.stepCompletionStatus[OnboardingStep.COMPANY_DETAILS] =
                this.validationResponse.startApplication?.complete || false

            this.stepCompletionStatus[OnboardingStep.COMPANY_CONTACTS] =
                this.validationResponse.startApplication?.complete || false

            this.stepCompletionStatus[OnboardingStep.OWNERS_DIRECTORS] =
                this.validationResponse.ownerAndDirectors?.complete || false

            this.stepCompletionStatus[OnboardingStep.BANK_DETAILS] =
                this.validationResponse.bankDetails?.complete || false

            this.stepCompletionStatus[OnboardingStep.OUTLETS_INFORMATION] =
                this.validationResponse.outletInformation?.complete || false
        } else {
            this.stepCompletionStatus[OnboardingStep.COMPANY_DETAILS] =
                this.checkCompanyDetailsComplete()
            this.stepCompletionStatus[OnboardingStep.COMPANY_CONTACTS] =
                this.checkCompanyContactsComplete()
            this.stepCompletionStatus[OnboardingStep.OWNERS_DIRECTORS] =
                this.checkOwnersDirectorsComplete()
            this.stepCompletionStatus[OnboardingStep.BANK_DETAILS] = this.checkBankDetailsComplete()

            this.stepCompletionStatus[OnboardingStep.OUTLETS_INFORMATION] =
                this.checkOutletsComplete()
        }
    }

    checkCompanyDetailsComplete(): boolean {
        if (!this.companyDetails) return false

        const entityType = this.companyDetails.entityType
        if (!entityType) return false

        const mandatoryFields = ONBOARDING_MANDATORY_FIELDS.companyDetails[entityType]

        if (!mandatoryFields) return false

        return mandatoryFields.every((field) => {
            const value = this.companyDetails[field]
            return value !== undefined && value !== null && value !== ''
        })
    }

    checkCompanyContactsComplete(): boolean {
        if (!this.companyContacts) return false

        const entityType = this.companyDetails?.entityType
        if (!entityType) return false

        const mandatoryFields = ONBOARDING_MANDATORY_FIELDS.companyContacts[entityType]
        if (!mandatoryFields) return false

        return mandatoryFields.every((field) => {
            if (['postalCode', 'addressLine1', 'townOrCity'].includes(field)) {
                const value = this.companyContacts.address?.[field]
                return value !== undefined && value !== null && value !== ''
            }

            const value = this.companyContacts[field]
            return value !== undefined && value !== null && value !== ''
        })
    }

    checkOwnersDirectorsComplete(): boolean {
        const entityType = this.companyDetails?.entityType
        if (!entityType) return false

        const mandatoryConfig = ONBOARDING_MANDATORY_FIELDS.ownersDirectors[entityType]
        if (!mandatoryConfig) return false

        const ownersComplete =
            this.owners.length > 0 &&
            this.owners.every((owner) => {
                if (!owner.isComplete) return false

                return mandatoryConfig.owner.every((field) => {
                    if (['postalCode', 'addressLine1', 'townOrCity'].includes(field)) {
                        const value = owner.address?.[field]
                        return value !== undefined && value !== null && value !== ''
                    }

                    const value = owner[field]
                    return value !== undefined && value !== null && value !== ''
                })
            })

        const directorsRequired = mandatoryConfig.director.length > 0
        const directorsComplete =
            !directorsRequired ||
            (this.directors.length > 0 &&
                this.directors.every((director) => {
                    if (!director.isComplete) return false

                    return mandatoryConfig.director.every((field) => {
                        if (['postalCode', 'addressLine1', 'townOrCity'].includes(field)) {
                            const value = director.address?.[field]
                            return value !== undefined && value !== null && value !== ''
                        }

                        const value = director[field]
                        return value !== undefined && value !== null && value !== ''
                    })
                }))

        return ownersComplete && directorsComplete
    }

    checkBankDetailsComplete(): boolean {
        if (
            !this.bankDetails ||
            !this.bankDetails.accounts ||
            this.bankDetails.accounts.length === 0
        ) {
            return false
        }

        const mandatoryFields = ONBOARDING_MANDATORY_FIELDS.bankDetails

        return this.bankDetails.accounts.every((account) => {
            if (!account.isComplete) return false

            return mandatoryFields.every((field) => {
                const value = account[field]
                return value !== undefined && value !== null && value !== ''
            })
        })
    }

    checkOutletsComplete(): boolean {
        if (!this.stores || this.stores.length === 0) return false

        const mandatoryFields = ONBOARDING_MANDATORY_FIELDS.outletsInformation

        return this.stores.every((outlet) => {
            return mandatoryFields.every((field) => {
                if (['postcode', 'addressLine1', 'townOrCity'].includes(field)) {
                    const value = outlet.address?.[field]
                    return value !== undefined && value !== null && value !== ''
                }

                const value = outlet[field]
                if (field.includes('Percentage') && value === 0) return true

                return value !== undefined && value !== null && value !== ''
            })
        })
    }

    getStepValidationDetails(step: OnboardingStep): {isComplete: boolean; missingFields: string[]} {
        switch (step) {
            case OnboardingStep.COMPANY_DETAILS:
                return this.getCompanyDetailsValidation()
            case OnboardingStep.COMPANY_CONTACTS:
                return this.getCompanyContactsValidation()
            case OnboardingStep.OWNERS_DIRECTORS:
                return this.getOwnersDirectorsValidation()
            case OnboardingStep.BANK_DETAILS:
                return this.getBankDetailsValidation()
            case OnboardingStep.OUTLETS_INFORMATION:
                return this.getOutletsValidation()
            default:
                return {isComplete: true, missingFields: []}
        }
    }

    private getCompanyDetailsValidation(): {isComplete: boolean; missingFields: string[]} {
        const missingFields: string[] = []

        if (!this.companyDetails) {
            return {isComplete: false, missingFields: ['All fields']}
        }

        const entityType = this.companyDetails.entityType
        if (!entityType) {
            missingFields.push('entityType')
            return {isComplete: false, missingFields}
        }

        const mandatoryFields = ONBOARDING_MANDATORY_FIELDS.companyDetails[entityType]
        if (!mandatoryFields) {
            return {isComplete: false, missingFields: ['Invalid entity type']}
        }

        mandatoryFields.forEach((field) => {
            const value = this.companyDetails[field]
            if (value === undefined || value === null || value === '') {
                missingFields.push(field)
            }
        })

        return {isComplete: missingFields.length === 0, missingFields}
    }

    private getCompanyContactsValidation(): {isComplete: boolean; missingFields: string[]} {
        const missingFields: string[] = []

        if (!this.companyContacts) {
            return {isComplete: false, missingFields: ['All fields']}
        }

        const entityType = this.companyDetails?.entityType
        if (!entityType) {
            return {isComplete: false, missingFields: ['Company details must be completed first']}
        }

        const mandatoryFields = ONBOARDING_MANDATORY_FIELDS.companyContacts[entityType]
        if (!mandatoryFields) {
            return {isComplete: false, missingFields: ['Invalid entity type']}
        }

        mandatoryFields.forEach((field) => {
            if (['postalCode', 'addressLine1', 'townOrCity'].includes(field)) {
                const value = this.companyContacts.address?.[field]
                if (value === undefined || value === null || value === '') {
                    missingFields.push(`address.${field}`)
                }
            } else {
                const value = this.companyContacts[field]
                if (value === undefined || value === null || value === '') {
                    missingFields.push(field)
                }
            }
        })

        return {isComplete: missingFields.length === 0, missingFields}
    }

    private getOwnersDirectorsValidation(): {isComplete: boolean; missingFields: string[]} {
        const missingFields: string[] = []

        const entityType = this.companyDetails?.entityType
        if (!entityType) {
            return {isComplete: false, missingFields: ['Company details must be completed first']}
        }

        const mandatoryConfig = ONBOARDING_MANDATORY_FIELDS.ownersDirectors[entityType]
        if (!mandatoryConfig) {
            return {isComplete: false, missingFields: ['Invalid entity type']}
        }

        if (this.owners.length === 0) {
            missingFields.push('At least one owner is required')
        } else {
            this.owners.forEach((owner, index) => {
                if (!owner.isComplete) {
                    missingFields.push(`Owner ${index + 1}: Not marked as complete`)
                }

                mandatoryConfig.owner.forEach((field) => {
                    if (['postalCode', 'addressLine1', 'townOrCity'].includes(field)) {
                        const value = owner.address?.[field]
                        if (value === undefined || value === null || value === '') {
                            missingFields.push(`Owner ${index + 1}: address.${field}`)
                        }
                    } else {
                        const value = owner[field]
                        if (value === undefined || value === null || value === '') {
                            missingFields.push(`Owner ${index + 1}: ${field}`)
                        }
                    }
                })
            })
        }

        if (mandatoryConfig.director.length > 0) {
            if (this.directors.length === 0) {
                missingFields.push('At least one director is required')
            } else {
                this.directors.forEach((director, index) => {
                    if (!director.isComplete) {
                        missingFields.push(`Director ${index + 1}: Not marked as complete`)
                    }

                    mandatoryConfig.director.forEach((field) => {
                        if (['postalCode', 'addressLine1', 'townOrCity'].includes(field)) {
                            const value = director.address?.[field]
                            if (value === undefined || value === null || value === '') {
                                missingFields.push(`Director ${index + 1}: address.${field}`)
                            }
                        } else {
                            const value = director[field]
                            if (value === undefined || value === null || value === '') {
                                missingFields.push(`Director ${index + 1}: ${field}`)
                            }
                        }
                    })
                })
            }
        }

        return {isComplete: missingFields.length === 0, missingFields}
    }

    private getBankDetailsValidation(): {isComplete: boolean; missingFields: string[]} {
        const missingFields: string[] = []

        if (
            !this.bankDetails ||
            !this.bankDetails.accounts ||
            this.bankDetails.accounts.length === 0
        ) {
            return {isComplete: false, missingFields: ['At least one bank account is required']}
        }

        const mandatoryFields = ONBOARDING_MANDATORY_FIELDS.bankDetails

        this.bankDetails.accounts.forEach((account, index) => {
            if (!account.isComplete) {
                missingFields.push(`Account ${index + 1}: Not marked as complete`)
            }

            mandatoryFields.forEach((field) => {
                const value = account[field]
                if (value === undefined || value === null || value === '') {
                    missingFields.push(`Account ${index + 1}: ${field}`)
                }
            })
        })

        return {isComplete: missingFields.length === 0, missingFields}
    }

    private getOutletsValidation(): {isComplete: boolean; missingFields: string[]} {
        const missingFields: string[] = []

        if (!this.stores || this.stores.length === 0) {
            return {isComplete: false, missingFields: ['At least one outlet is required']}
        }

        const mandatoryFields = ONBOARDING_MANDATORY_FIELDS.outletsInformation

        this.stores.forEach((outlet, index) => {
            mandatoryFields.forEach((field) => {
                if (['postcode', 'addressLine1', 'townOrCity'].includes(field)) {
                    const value = outlet.storeAddress?.[field]
                    if (value === undefined || value === null || value === '') {
                        missingFields.push(`Outlet ${index + 1}: address.${field}`)
                    }
                } else {
                    const value = outlet[field]
                    if (!(field.includes('Percentage') && value === 0)) {
                        if (value === undefined || value === null || value === '') {
                            missingFields.push(`Outlet ${index + 1}: ${field}`)
                        }
                    }
                }
            })
        })

        return {isComplete: missingFields.length === 0, missingFields}
    }

    async saveProgress(): Promise<void> {}

    async loadOnboardingData(processKey?: string): Promise<void> {
        try {
            this.isLoading = true

            if (processKey) {
                try {
                    await this.loadSelfOnboardingApplicationData()

                    const validationResult = await this.validateApplication()
                    this.validationResponse = validationResult

                    const targetStep = this.determineCurrentStepFromValidation(validationResult)

                    this.isNavigatingFromValidation = true
                    this.goToStep(targetStep)

                    setTimeout(() => {
                        this.isNavigatingFromValidation = false
                    }, 2000)
                    return
                } catch (error) {}
            }

            this.companyDetails = undefined
            this.companyContacts = undefined
            this.owners = []
            this.directors = []
            this.bankDetails = undefined
            this.rawApplicationData = undefined
            this.goToStep(OnboardingStep.START)

            Object.keys(this.stepCompletionStatus).forEach((stepKey) => {
                const step = parseInt(stepKey) as OnboardingStep
                this.stepCompletionStatus[step] = false
            })

            this.stepCompletionStatus[OnboardingStep.START] = true

            this.updateStepCompletionStatus()
        } catch (error) {
            throw error
        } finally {
            this.isLoading = false
        }
    }

    async checkSelfOnboardingProcessStatus(processKey?: string): Promise<void> {
        try {
            this.isLoading = true
            const keyToUse = processKey || this.processKey
            if (!keyToUse) {
                throw new Error('Process key is required to check status')
            }

            const {result} = await fetchSelfOnboardingProcessStatus(keyToUse)

            if (!result) {
                this.processStatus = undefined
                this.processStages = undefined
                this.goToStep(OnboardingStep.START)
                return
            }

            const response = result as OnboardingProcessStatusResponse

            if (!response.activeProcessFound) {
                this.processStatus = undefined
                this.processStages = undefined
                this.goToStep(OnboardingStep.START)
                return
            }

            if (response.activeProcess) {
                const activeProcess = response.activeProcess

                this.processStatus = activeProcess.status
                this.processStages = activeProcess.stages
            } else {
                this.processStatus = undefined
                this.processStages = undefined
                this.goToStep(OnboardingStep.START)
                return
            }

            if (!this.processKey) {
            }

            if (this.processStages?.applicationSubmission === 'completed') {
                this.goToStep(OnboardingStep.APPLICATION_PROGRESS)
                return
            }

            if (this.processStatus) {
                await this.loadOnboardingData(keyToUse)
            }
        } catch (error: any) {
            throw error
        } finally {
            this.isLoading = false
        }
    }

    async initializeSelfOnboardingProcess(): Promise<void> {
        try {
            if (this.isInitialized) {
                return
            }

            getGlobalLoaderStore().showGlobalLoader()

            if (this.processKey) {
                this.isInitialized = true
                try {
                    await this.checkSelfOnboardingProcessStatus(this.processKey)
                    return
                } catch (error: any) {
                    this.processStatus = undefined
                    this.goToStep(OnboardingStep.START)
                }
            }
        } catch (error) {
            this.isInitialized = false
        } finally {
            getGlobalLoaderStore().hideGlobalLoader()
        }
    }

    async startSelfOnboardingApplication(): Promise<void> {
        try {
            this.stepCompletionStatus[OnboardingStep.START] = true
            this.goToStep(OnboardingStep.COMPANY_DETAILS)
        } catch (error: any) {
            throw error
        }
    }

    private async ensureOriginalCompanyInfo(): Promise<void> {
        if (
            this.originalCompanyInfo ||
            (this.companyDetails?.entityType !== BusinessStructure.SOLE_TRADER &&
                this.companyDetails?.entityType !== BusinessStructure.PARTNERSHIP)
        ) {
            return
        }

        const companyNumber = this.companyDetails?.companyNumber
        if (!companyNumber) {
            return
        }

        try {
            this._originalCompanyInfo = await this.getCompanyInfo(companyNumber)
        } catch (error) {
            console.warn('Failed to fetch original company info:', error)
        }
    }

    async getPreFilledFromApiForDirector(director: any): Promise<DirectorData['preFilledFromApi']> {
        if (this.isCompanyOrLLPFlow) {
            await this.ensureOriginalCompanyInfo()
        }

        const originalData = this.originalCompanyInfo
        if (!originalData || !originalData.companyDirectors) {
            return {}
        }

        const originalDirector = originalData.companyDirectors.find(
            (d: any) => d.firstName === director.firstName && d.surname === director.surname
        )

        if (!originalDirector) {
            return {}
        }

        return {
            title: false,
            firstName: !!originalDirector.firstName,
            middleName: !!originalDirector.middleName,
            surname: !!originalDirector.surname,
            dateOfBirth: !!originalDirector.dateOfBirth,
            nationality: !!originalDirector.nationality,
            placeOfBirth: !!originalDirector.placeOfBirth,
            gender: false,
            emailAddress: !!originalDirector.emailAddress,
            telephoneNumber: !!originalDirector.telephoneNumber
        }
    }

    async getPreFilledFromApiForOwner(owner: any): Promise<OwnerData['preFilledFromApi']> {
        if (this.isCompanyOrLLPFlow) {
            await this.ensureOriginalCompanyInfo()
        }

        const originalData = this.originalCompanyInfo
        if (!originalData || !originalData.individualShareholders) {
            return {}
        }

        const originalOwner = originalData.individualShareholders.find(
            (o: any) => o.firstName === owner.firstName && o.surname === owner.surname
        )

        if (!originalOwner) {
            return {
                middleName: false
            }
        }

        const baseFields = {
            title: false,
            firstName: !!originalOwner.firstName,
            middleName: !!originalOwner.middleName,
            surname: !!originalOwner.surname,
            dateOfBirth: !!originalOwner.dateOfBirth,
            nationality: !!originalOwner.nationality,
            placeOfBirth: !!originalOwner.placeOfBirth,
            gender: false,
            emailAddress: !!originalOwner.emailAddress,
            telephoneNumber: !!originalOwner.telephoneNumber,
            residentialAddress: !!originalOwner.residentialAddress
        }

        if (this.isSoleTraderFlow) {
            return baseFields
        }

        return {
            ...baseFields,
            ownershipPercentage: !!originalOwner.ownershipPercentage
        }
    }

    async loadSelfOnboardingApplicationData(): Promise<void> {
        try {
            if (this.skipFetchingExistingData) {
                return
            }

            const applicationData = await fetchSelfOnboardingApplicationDetails(this.processKey)

            if (!applicationData || typeof applicationData !== 'object') {
                return
            }

            this.rawApplicationData = applicationData

            if (
                applicationData.companyProfile &&
                typeof applicationData.companyProfile === 'object'
            ) {
                const profile = applicationData.companyProfile

                const businessStructure =
                    typeof applicationData.businessStructure === 'string'
                        ? applicationData.businessStructure
                        : BusinessStructure.COMPANY

                runInAction(() => {
                    this.companyDetails = {
                        companyName: this.getStringProperty(profile, 'companyName'),
                        companyTradeName: this.getStringProperty(
                            profile,
                            'companyTradeName',
                            'tradingName'
                        ),
                        companyNumber: this.getStringProperty(
                            profile,
                            'companyNumber',
                            'registrationNumber'
                        ),
                        dateOfCreation: this.getStringProperty(
                            profile,
                            'dateOfCreation',
                            'registrationDate'
                        ),
                        numberOfEmployees: this.getStringProperty(
                            profile,
                            'numberOfEmployees',
                            'employeesNumber'
                        ),
                        entityType: businessStructure,
                        customEntityType:
                            businessStructure === 'other'
                                ? this.getStringProperty(profile, 'customEntityType')
                                : undefined
                    } as CompanyDetailsFormData
                })

                if (profile.companyAddress && typeof profile.companyAddress === 'object') {
                    const address = profile.companyAddress
                    runInAction(() => {
                        this.companyContacts = {
                            companyWebsite: this.getStringProperty(profile, 'companyWebsite'),
                            emailAddress: this.getStringProperty(profile, 'emailAddress'),
                            telephoneNumber: this.getStringProperty(profile, 'telephoneNumber'),
                            address: {
                                addressLine1: this.getStringProperty(address, 'addressLine1'),
                                addressLine2: this.getStringProperty(address, 'addressLine2'),
                                townOrCity: this.getStringProperty(address, 'townOrCity'),
                                postalCode: this.getStringProperty(address, 'postalCode')
                            }
                        } as CompanyContactsFormData
                    })
                }
            } else {
                runInAction(() => {
                    this.companyDetails = undefined
                    this.companyContacts = undefined
                })
            }

            if (
                Array.isArray(applicationData.companyDirectors) &&
                applicationData.companyDirectors.length > 0
            ) {
                const newDirectors = await Promise.all(
                    applicationData.companyDirectors.map(async (director: any, index: number) => {
                        return {
                            ...director,
                            id: director.id || uuid(),
                            uploadDocManually: director.uploadDocManually || false,
                            officerVerificationResult: director.officerVerificationResult || null,
                            isComplete:
                                director.verificationResponse?.isVerified === true ||
                                director.isVerified === true ||
                                director.uploadDocManually === true ||
                                (director.documents && director.documents.length > 0),
                            preFilledFromApi: await this.getPreFilledFromApiForDirector(director),
                            address: director.residentialAddress
                                ? {
                                      addressLine1: director.residentialAddress.addressLine1 || '',
                                      addressLine2: director.residentialAddress.addressLine2 || '',
                                      townOrCity: director.residentialAddress.townOrCity || '',
                                      postalCode: director.residentialAddress.postalCode || '',
                                      country: director.residentialAddress.country
                                  }
                                : director.address
                        }
                    })
                )

                this.directors = newDirectors.map((newDirector) => {
                    const existingDirector = this.directors.find((d) => d.id === newDirector.id)
                    if (existingDirector && this.directorsFormChanged.get(newDirector.id)) {
                        return {
                            ...existingDirector,
                            verificationResponse:
                                newDirector.verificationResponse ||
                                existingDirector.verificationResponse,
                            officerVerificationResult:
                                newDirector.officerVerificationResult || null,
                            documents: newDirector.documents || existingDirector.documents,
                            isComplete: newDirector.isComplete,
                            uploadDocManually:
                                newDirector.uploadDocManually || existingDirector.uploadDocManually,
                            preFilledFromApi:
                                newDirector.preFilledFromApi || existingDirector.preFilledFromApi
                        }
                    }
                    return newDirector
                })

                this.ensureDirectorSignatory()
            }

            if (this.isPartnershipFlow && this.owners.length > 0) {
                this.ensurePartnerSignatory()
            }

            const businessStructure =
                applicationData.businessStructure || applicationData.companyProfile?.entityType
            const isSoleTrader = businessStructure === BusinessStructure.SOLE_TRADER
            const isPartnership = businessStructure === BusinessStructure.PARTNERSHIP

            let ownersArray: any[] = []

            if (isSoleTrader || isPartnership) {
                ownersArray = Array.isArray(applicationData.businessOwners)
                    ? applicationData.businessOwners
                    : []
            } else {
                ownersArray = Array.isArray(applicationData.individualShareholders)
                    ? applicationData.individualShareholders
                    : []
            }

            if (ownersArray.length === 0 && Array.isArray(applicationData.owners)) {
                ownersArray = applicationData.owners
            }

            if (ownersArray.length > 0) {
                const individualOwners = ownersArray.filter((owner) => !owner.isCompany)
                const corporateOwnersFromArray = ownersArray.filter((owner) => owner.isCompany)

                if (individualOwners.length > 0) {
                    const newOwners = await Promise.all(
                        individualOwners.map(async (owner: any, index: number) => {
                            return {
                                ...owner,
                                id: owner.id || uuid(),
                                uploadDocManually: owner.uploadDocManually || false,
                                isComplete:
                                    owner.verificationResponse?.isVerified === true ||
                                    owner.isVerified === true ||
                                    owner.uploadDocManually === true ||
                                    (owner.documents && owner.documents.length > 0),
                                preFilledFromApi: await this.getPreFilledFromApiForOwner(owner),
                                address: owner.residentialAddress
                                    ? {
                                          addressLine1: owner.residentialAddress.addressLine1 || '',
                                          addressLine2: owner.residentialAddress.addressLine2 || '',
                                          townOrCity: owner.residentialAddress.townOrCity || '',
                                          postalCode: owner.residentialAddress.postalCode || '',
                                          country: owner.residentialAddress.country
                                      }
                                    : owner.address
                            }
                        })
                    )

                    runInAction(() => {
                        this.owners = newOwners.map((newOwner) => {
                            const existingOwner = this.owners.find((o) => o.id === newOwner.id)
                            if (existingOwner && this.ownersFormChanged.get(newOwner.id)) {
                                return {
                                    ...existingOwner,
                                    officerVerificationResult:
                                        newOwner.officerVerificationResult ||
                                        existingOwner.officerVerificationResult,
                                    documents: newOwner.documents || existingOwner.documents,
                                    isComplete: newOwner.isComplete,
                                    uploadDocManually:
                                        newOwner.uploadDocManually ||
                                        existingOwner.uploadDocManually,
                                    preFilledFromApi:
                                        newOwner.preFilledFromApi || existingOwner.preFilledFromApi
                                }
                            }
                            return newOwner
                        })
                    })
                }

                if (corporateOwnersFromArray.length > 0) {
                    const newCorporateOwners = corporateOwnersFromArray.map(
                        (owner: any, index: number) => ({
                            ...owner,
                            id: owner.id || uuid(),
                            companyName: owner.companyName || '',
                            companyNumber: owner.companyNumber || '',
                            ownershipPercentage: owner.ownershipPercentage || 0,
                            isComplete:
                                owner.isVerified === true || owner.uploadDocManually === true,
                            deletionAllowed: owner.deletionAllowed !== false
                        })
                    )

                    runInAction(() => {
                        const existingCorporateOwners = [...this.corporateOwners]

                        newCorporateOwners.forEach((newCorporateOwner) => {
                            const existingIndex = existingCorporateOwners.findIndex(
                                (co) => co.id === newCorporateOwner.id
                            )
                            if (existingIndex >= 0) {
                                existingCorporateOwners[existingIndex] = {
                                    ...existingCorporateOwners[existingIndex],
                                    ...newCorporateOwner
                                }
                            } else {
                                existingCorporateOwners.push(newCorporateOwner)
                            }
                        })

                        this.corporateOwners = existingCorporateOwners
                    })
                }
            }

            if (
                Array.isArray(applicationData.corporateEntityShareholders) &&
                applicationData.corporateEntityShareholders.length > 0
            ) {
                this.corporateOwners = applicationData.corporateEntityShareholders.map(
                    (corporateOwner: any, index: number) => ({
                        id: corporateOwner.id || uuid(),
                        companyName: corporateOwner.companyName || '',
                        companyNumber: corporateOwner.companyNumber || '',
                        ownershipPercentage: corporateOwner.ownershipPercentage || 0,
                        isComplete: true,
                        isVerified: corporateOwner.isVerified || false,
                        uploadDocManually: corporateOwner.uploadDocManually || false,
                        deletionAllowed: corporateOwner.deletionAllowed !== false,
                        fromApi: corporateOwner.fromApi !== false,
                        officerVerificationResult: corporateOwner.officerVerificationResult || null,
                        agreementSignatory: corporateOwner.agreementSignatory || null,
                        jumioCheckResults: corporateOwner.jumioCheckResults || null,
                        jumioRequestSent: corporateOwner.jumioRequestSent || null,
                        preFilledFromApi: {
                            companyName: !!corporateOwner.companyName,
                            companyNumber: !!corporateOwner.companyNumber,
                            ownershipPercentage:
                                corporateOwner.ownershipPercentage !== undefined &&
                                corporateOwner.ownershipPercentage !== null
                        }
                    })
                )
            }

            if (
                (applicationData as any).stages &&
                typeof (applicationData as any).stages === 'object' &&
                !this.processStages
            ) {
                this.processStages = (applicationData as any).stages
            }

            if (
                Array.isArray(applicationData.bankAccounts) &&
                applicationData.bankAccounts.length > 0
            ) {
                this.bankDetails = {
                    accounts: applicationData.bankAccounts.map((account: any) => ({
                        ...account,
                        id: account.id || String(Date.now() + Math.random()),
                        bankAccountNumber: this.getStringProperty(
                            account,
                            'bankAccountNumber',
                            'accountNumber'
                        ),
                        sortCode: this.getStringProperty(account, 'sortCode'),
                        accountName: this.getStringProperty(
                            account,
                            'accountName',
                            'bankAccountName'
                        ),
                        bankName: this.getStringProperty(account, 'bankName'),
                        bankLogo: account.bankLogo,
                        isComplete: account.isVerified || false,
                        isVerified:
                            account.isVerified ||
                            account.creditSafeVerificationResults?.verified ||
                            false,
                        isDefault:
                            typeof account.isDefault === 'boolean' ? account.isDefault : false,
                        uploadDocManually: account.uploadDocManually || false
                    }))
                } as BankDetailsFormData
            }

            if (Array.isArray(applicationData.stores) && applicationData.stores.length > 0) {
                this.stores = applicationData.stores.map((store: any) => ({
                    id: store.id,
                    tradingName: store.storeName || '',
                    systemDescriptor: store.systemDescriptor || '',
                    settlementsAccount: store.bankInfo?.bankAccountNumber || '',
                    directDebitAccount: store.directDebitBankInfo?.bankAccountNumber || undefined,
                    directDebitChargesAccount: store.directDebitChargesAccount || undefined,
                    useDirectDebit: !!store.directDebitBankInfo,
                    productsYouSell: store.natureOfBusiness || '',
                    address: {
                        postalCode: store.storeAddress?.postalCode || '',
                        addressLine1: store.storeAddress?.addressLine1 || '',
                        addressLine2: store.storeAddress?.addressLine2 || '',
                        townOrCity: store.storeAddress?.townOrCity || ''
                    },
                    sameAsRegistered: false,
                    isSeasonalBusiness:
                        store.financialInfo?.businessModelQuestions?.seasonalBusiness?.isTrue ||
                        false,
                    q1Percentage:
                        store.financialInfo?.businessModelQuestions?.seasonalBusiness?.quarter1,
                    q2Percentage:
                        store.financialInfo?.businessModelQuestions?.seasonalBusiness?.quarter2,
                    q3Percentage:
                        store.financialInfo?.businessModelQuestions?.seasonalBusiness?.quarter3,
                    q4Percentage:
                        store.financialInfo?.businessModelQuestions?.seasonalBusiness?.quarter4,
                    cardAnnualTurnover: store.financialInfo?.expectedAnnualCardTurnover
                        ? store.financialInfo.expectedAnnualCardTurnover
                        : undefined,
                    averageTransactionValue: store.financialInfo?.averageTransactionValue
                        ? store.financialInfo.averageTransactionValue
                        : undefined,
                    highestTransactionValue: store.financialInfo?.highestTransactionValue
                        ? store.financialInfo.highestTransactionValue
                        : undefined,
                    posPercentage: store.processingMethods?.find((pm: any) => pm.name === 'pos')
                        ?.percent,
                    motoPercentage: store.processingMethods?.find((pm: any) => pm.name === 'moto')
                        ?.percent,
                    virtualTerminalPercentage: store.processingMethods?.find(
                        (pm: any) => pm.name === 'virtualTerminal'
                    )?.percent,
                    paymentLinkPercentage: store.processingMethods?.find(
                        (pm: any) => pm.name === 'payByLink'
                    )?.percent,
                    paymentAppsPercentage: store.processingMethods?.find(
                        (pm: any) => pm.name === 'payByApp'
                    )?.percent
                }))
            }

            this.updateStepCompletionStatus()

            if (!this.hasExplicitStepFromNavigation) {
                runInAction(() => {
                    this.currentStep = this.determineCurrentStepFromData()
                })
            }
        } catch (error) {}
    }

    private getStringProperty(obj: any, ...propertyNames: string[]): string {
        if (!obj || typeof obj !== 'object') {
            return ''
        }

        for (const propName of propertyNames) {
            if (typeof obj[propName] === 'string') {
                return obj[propName]
            }
        }

        return ''
    }

    private determineCurrentStepFromData(): OnboardingStep {
        if (!this.companyDetails || !this.isCompanyDetailsComplete()) {
            return OnboardingStep.COMPANY_DETAILS
        }

        if (!this.companyContacts || !this.isCompanyContactsComplete()) {
            return OnboardingStep.COMPANY_CONTACTS
        }

        if (!this.isOwnersDirectorsComplete()) {
            return OnboardingStep.OWNERS_DIRECTORS
        }

        if (!this.bankDetails || !this.bankDetails.accounts?.length) {
            return OnboardingStep.BANK_DETAILS
        }

        return OnboardingStep.REVIEW_SUBMIT
    }

    private isCompanyDetailsComplete(): boolean {
        if (!this.companyDetails) return false
        return !!(
            this.companyDetails.companyName &&
            this.companyDetails.companyTradeName &&
            this.companyDetails.entityType
        )
    }

    private isCompanyContactsComplete(): boolean {
        if (!this.companyContacts) return false
        return !!(
            this.companyContacts.emailAddress &&
            this.companyContacts.address?.addressLine1 &&
            this.companyContacts.address?.townOrCity &&
            this.companyContacts.address?.postalCode
        )
    }

    private isOwnersDirectorsComplete(): boolean {
        // For partnerships, check if there are partners with a signatory
        if (this.isPartnershipFlow) {
            return this.owners.length > 0 && this.owners.some((owner) => owner.agreementSignatory)
        }

        // For other entity types, check directors
        return (
            this.directors.length > 0 &&
            this.directors.some((director) => director.agreementSignatory)
        )
    }

    private ensureDirectorSignatory(): void {
        if (this.directors.length > 0 && !this.directors.some((d) => d.agreementSignatory)) {
            this.directors[0].agreementSignatory = true
        }
    }

    private ensurePartnerSignatory(): void {
        if (this.owners.length > 0 && !this.owners.some((o) => o.agreementSignatory)) {
            this.owners[0].agreementSignatory = true
        }
    }

    determineCurrentStepFromValidation(validation: ApplicationValidationResponse): OnboardingStep {
        if (validation.startApplication?.complete) {
            this.stepCompletionStatus[OnboardingStep.COMPANY_DETAILS] = true
            this.stepCompletionStatus[OnboardingStep.COMPANY_CONTACTS] = true
        }

        const ownersDirectorsValidation = validation.ownerAndDirectors
        if (ownersDirectorsValidation) {
            this.stepCompletionStatus[OnboardingStep.OWNERS_DIRECTORS] =
                ownersDirectorsValidation.complete
        }

        if (validation.bankDetails) {
            this.stepCompletionStatus[OnboardingStep.BANK_DETAILS] = validation.bankDetails.complete
        }

        if (validation.outletInformation) {
            this.stepCompletionStatus[OnboardingStep.OUTLETS_INFORMATION] =
                validation.outletInformation.complete
        }

        if (!validation.startApplication?.complete) {
            if (!this.companyDetails) {
                return OnboardingStep.COMPANY_DETAILS
            } else if (!this.companyContacts) {
                return OnboardingStep.COMPANY_CONTACTS
            }

            return OnboardingStep.COMPANY_DETAILS
        }

        if (ownersDirectorsValidation && !ownersDirectorsValidation.complete) {
            return OnboardingStep.OWNERS_DIRECTORS
        }

        if (!validation.bankDetails?.complete) {
            return OnboardingStep.BANK_DETAILS
        }

        if (!validation.outletInformation?.complete) {
            return OnboardingStep.OUTLETS_INFORMATION
        }

        return OnboardingStep.REVIEW_SUBMIT
    }

    async searchCompaniesByName(name: string): Promise<any> {
        try {
            this.isLoading = true
            const result = await fetchSelfOnboardingCompanyByName(name)

            if (!result) {
                return {items: []}
            }

            if (Array.isArray(result)) {
                return {items: result}
            }

            if (result.items && Array.isArray(result.items)) {
                return result
            }

            return {items: []}
        } catch (error: any) {
            return {items: []}
        } finally {
            this.isLoading = false
        }
    }

    async getCompanyInfo(companyId: string): Promise<CompanyInfoResponse> {
        try {
            this.isLoading = true

            return await fetchCompanyInfo(companyId)
        } catch (error: unknown) {
            throw error
        } finally {
            this.isLoading = false
        }
    }

    async searchAddressByPostalCode(postcode: string): Promise<any[]> {
        try {
            this.isLoading = true
            const result = await fetchSelfOnboardingAddressByPostalCode(postcode)
            return result?.addresses || []
        } catch (error: any) {
            return []
        } finally {
            this.isLoading = false
        }
    }

    async getDictionary(category: string): Promise<any[]> {
        try {
            this.isLoading = true
            const result = await fetchSelfOnboardingDictionary(category)
            return result.items || []
        } catch (error: any) {
            return []
        } finally {
            this.isLoading = false
        }
    }

    async validateApplication(data?: any): Promise<ApplicationValidationResponse> {
        try {
            this.isLoading = true

            const authStore = subscribe<AuthStoreInterface>(AuthStoreSymbol)
            const userEmail = getCurrentUserEmail(authStore as any)

            if (this.rawApplicationData && !data) {
                return await validateSelfOnboardingApplication(this.rawApplicationData)
            }

            this.updateStepCompletionStatus()

            console.log(
                'this.companyDetails',
                this.companyDetails?.charityNumber,
                this.companyDetails?.charityCommission
            )
            const charityNumber =
                this.rawApplicationData?.companyProfile?.charityNumber ||
                this.companyDetails?.charityNumber
            const charityCommission =
                this.rawApplicationData?.companyProfile?.charityCommission ||
                this.companyDetails?.charityCommission
            console.log('this.companyDetails true', charityNumber, charityCommission)

            const validationData = data || {
                existingClient: false,
                businessStructure: this.companyDetails?.entityType || BusinessStructure.COMPANY,
                paymentSolution: '',
                isFastTrack: false,
                isMsaSigned: false,
                processSettings: {
                    bankDetailScheme: 'UK'
                },
                companyProfile: {
                    companyAddress: {
                        postalCode: this.companyContacts?.address?.postalCode || '',
                        addressLine1: this.companyContacts?.address?.addressLine1 || '',
                        addressLine2: this.companyContacts?.address?.addressLine2 || '',
                        townOrCity: this.companyContacts?.address?.townOrCity || ''
                    },
                    numberOfEmployees: this.companyDetails?.numberOfEmployees || '2',
                    tradeOutsideEea: false,
                    companyName: this.companyDetails?.companyName || '',
                    companyTradeName: this.companyDetails?.companyTradeName || '',
                    companyNumber: this.companyDetails?.companyNumber || '',
                    dateOfCreation: this.companyDetails?.dateOfCreation || '',
                    companyWebsite: this.companyContacts?.companyWebsite || '',
                    emailAddress: this.companyContacts?.emailAddress || '',
                    telephoneNumber: this.companyContacts?.telephoneNumber || '',
                    bankAccountNumber: this.bankDetails?.accounts?.[0]?.bankAccountNumber || '',
                    sortCode: this.bankDetails?.accounts?.[0]?.sortCode || '',
                    accountName: this.bankDetails?.accounts?.[0]?.accountName || '',
                    bankName: this.bankDetails?.accounts?.[0]?.bankName || '',
                    bankAccountType: this.bankDetails?.accounts?.[0]?.bankAccountType || 'business',
                    bankAccountVerificationResponse:
                        this.bankDetails?.accounts?.[0]?.verificationResponse || null,
                    companyType: this.companyDetails?.entityType || BusinessStructure.COMPANY,
                    charityNumber,
                    charityCommission
                },
                version: '2.1',
                applicantInfo: {
                    initiatorEmail: userEmail
                },
                productVersion: '2'
            }

            if (this.currentStep >= OnboardingStep.OWNERS_DIRECTORS) {
                validationData.individualShareholders = this.owners.map((owner) => ({
                    ...owner,
                    deletionAllowed: false
                }))
                validationData.companyDirectors = this.directors.map((director) => ({
                    ...director,
                    deletionAllowed: false
                }))
            }

            // Include stores if we're at outlets step or later, OR if stores exist (fallback)
            if (this.currentStep >= OnboardingStep.OUTLETS_INFORMATION || this.stores.length > 0) {
                validationData.stores = this.stores.map((store) => {
                    // Find the bank account that matches the settlements account
                    const settlementsBankAccount = this.bankDetails?.accounts?.find(
                        (account) => account.bankAccountNumber === store.settlementsAccount
                    )
                    const directDebitBankAccount = store.useDirectDebit
                        ? settlementsBankAccount
                        : this.bankDetails?.accounts?.find(
                              (account) => account.bankAccountNumber === store.directDebitAccount
                          )

                    return {
                        storeName: store.tradingName,
                        storeAddress: {
                            addressLine1: store.address.addressLine1,
                            addressLine2: store.address.addressLine2,
                            locality: '',
                            townOrCity: store.address.townOrCity,
                            postcode: store.address.postalCode
                        },
                        address: {
                            addressLine1: store.address.addressLine1,
                            addressLine2: store.address.addressLine2,
                            townOrCity: store.address.townOrCity,
                            postalCode: store.address.postalCode,
                            postcode: store.address.postalCode,
                            country: store.address.country || 'United Kingdom'
                        },
                        systemDescriptor: store.systemDescriptor,
                        requireOriginalCountry: false,
                        bankInfo: {
                            id: `${Date.now()}.${Math.random()}`,
                            bankAccountNumber:
                                settlementsBankAccount?.bankAccountNumber ||
                                store.settlementsAccount,
                            sortCode: settlementsBankAccount?.sortCode || '',
                            accountName: settlementsBankAccount?.accountName || '',
                            bankName: settlementsBankAccount?.bankName || '',
                            bankAccountType: settlementsBankAccount?.bankAccountType || 'business',
                            isComplete: true,
                            isVerified: true,
                            isDefault: false,
                            uploadDocManually: true
                        },
                        directDebitBankInfo: {
                            id: `${Date.now()}.${Math.random()}`,
                            bankAccountNumber:
                                directDebitBankAccount?.bankAccountNumber ||
                                (store.useDirectDebit
                                    ? store.settlementsAccount
                                    : store.directDebitAccount),
                            sortCode: directDebitBankAccount?.sortCode || '',
                            accountName: directDebitBankAccount?.accountName || '',
                            bankName: directDebitBankAccount?.bankName || '',
                            bankAccountType: directDebitBankAccount?.bankAccountType || 'business',
                            isComplete: true,
                            isVerified: true,
                            isDefault: false,
                            uploadDocManually: true
                        },
                        processingMethods: getProcessingMethodsTemplate().map((method) => {
                            switch (method.name) {
                                case 'pos':
                                    return {...method, percent: store.posPercentage || 0}
                                case 'moto':
                                    return {...method, percent: store.motoPercentage || 0}
                                case 'ecom':
                                    return {...method, percent: 0}
                                case 'payByLink':
                                    return {...method, percent: store.paymentLinkPercentage || 0}
                                case 'payByApp':
                                    return {...method, percent: store.paymentAppsPercentage || 0}
                                case 'virtualTerminal':
                                    return {
                                        ...method,
                                        percent: store.virtualTerminalPercentage || 0
                                    }
                                default:
                                    return {...method, percent: 0}
                            }
                        }),
                        advancePayments: [],
                        natureOfBusiness: store.productsYouSell
                    }
                })
            }

            const result = await validateSelfOnboardingApplication(validationData)
            this.updateStepCompletionStatus()
            return result
        } catch (error: any) {
            throw error
        } finally {
            this.isLoading = false
        }
    }

    async reloadApplicationData(callerName: string): Promise<void> {
        if (!this.processKey) {
            return
        }

        try {
            this.isLoading = true
            await this.loadSelfOnboardingApplicationData()
        } catch (error) {
        } finally {
            this.isLoading = false
        }
    }

    async verifyPerson(
        personData: any,
        role: 'business-owner' | 'director' | 'individual-shareholder'
    ): Promise<{verified: boolean; verificationResponse?: VerifyPersonResponse}> {
        try {
            this.isSubmitting = true

            // Validate date of birth
            const dateOfBirth = dayjs(personData?.dateOfBirth)
            if (!dateOfBirth.isValid()) {
                throw new Error('Invalid date of birth provided')
            }

            const verifyRequest = {
                role,
                companyType: this.companyDetails?.entityType || 'company',
                person: {
                    ownershipPercentage: personData.ownershipPercentage,
                    title: personData.title,
                    firstName: personData.firstName,
                    middleName: personData.middleName || '',
                    surname: personData.surname,
                    dateOfBirth: {
                        year: dateOfBirth.year(),
                        month: dateOfBirth.month() + 1,
                        day: dateOfBirth.date()
                    },
                    gender: personData.gender,
                    nationality: personData.nationality,
                    placeOfBirth: personData.placeOfBirth,
                    emailAddress: personData.emailAddress,
                    telephoneNumber: personData.telephoneNumber,
                    residentialAddress: {
                        country: personData.address?.country || 'United Kingdom',
                        postalCode: personData.address?.postalCode || '',
                        addressLine1: personData.address?.addressLine1 || '',
                        addressLine2: personData.address?.addressLine2 || '',
                        townOrCity: personData.address?.townOrCity || ''
                    }
                }
            }

            const result = await verifySelfOnboardingPerson(verifyRequest)

            if (result.isVerified) {
                return {
                    verified: true,
                    verificationResponse: result
                }
            } else {
                return {
                    verified: false,
                    verificationResponse: result
                }
            }
        } catch (error: any) {
            console.error('Verification API error:', error)
            // Return a response that indicates verification failed and documents are needed
            return {
                verified: false,
                verificationResponse: {
                    isVerified: false,
                    addressVerifications: [],
                    idVerifications: [],
                    errors: ['Verification failed - please upload documents'],
                    warnings: []
                }
            }
        } finally {
            this.isSubmitting = false
        }
    }

    async loadBusinessStructures(): Promise<void> {
        try {
            this.businessStructures = await this.getDictionary('d_business_structure')
        } catch (error) {
            console.error('Failed to fetch business structures:', error)
            this.businessStructures = []
        }
    }

    mapCompanyType(apiType: string): string {
        if (!apiType || this.businessStructures.length === 0) {
            return BusinessStructure.COMPANY
        }

        const normalizedType = apiType.toLowerCase()

        const exactMatch = this.businessStructures.find(
            (structure) => structure.code.toLowerCase() === normalizedType
        )
        if (exactMatch) {
            return exactMatch.code
        }

        const valueMatch = this.businessStructures.find((structure) => {
            const value = structure.value.toLowerCase()
            return value.includes(normalizedType) || normalizedType.includes(value.replace(' ', ''))
        })
        if (valueMatch) {
            return valueMatch.code
        }

        return this.businessStructures.some((s) => s.code === BusinessStructure.COMPANY)
            ? BusinessStructure.COMPANY
            : BusinessStructure.OTHER
    }

    formatDateOfCreation(dateString: string): string {
        if (!dateString) return ''

        if (dateString === '0001-01-01T00:00:00Z' || dateString.startsWith('0001-01-01')) {
            return ''
        }

        const date = dayjs(dateString)
        if (!date.isValid()) return ''

        return date.format('YYYY-MM-DD')
    }

    isValidDateForForm(dateString: string): boolean {
        if (!dateString) return false

        if (dateString === '0001-01-01T00:00:00Z' || dateString.startsWith('0001-01-01')) {
            return false
        }

        const date = dayjs(dateString)
        return date.isValid()
    }

    async handleCompanyDetailsFormSave(
        formValues: any,
        companyDetailsStore: any,
        showConfirmationCallback: () => Promise<boolean>
    ): Promise<void> {
        if (companyDetailsStore.isDissolvedCompany) {
            throw new Error('DISSOLVED_COMPANY')
        }

        companyDetailsStore.setIsLoading(true)
        try {
            const baseFormData = {
                entityType: formValues.entityType,
                ...(formValues.entityType === BusinessStructure.OTHER
                    ? {customEntityType: formValues.customEntityType}
                    : {}),
                companyName: formValues.companyName,
                companyTradeName: formValues.companyTradeName,
                legalBusinessName: formValues.legalBusinessName,
                companyNumber: formValues.registrationNumber,
                dateOfCreation:
                    companyDetailsStore.selectedCompany?.dateOfCreation ||
                    formValues.dateOfCreation?.format('YYYY-MM-DD') ||
                    '',
                numberOfEmployees: formValues.numberOfEmployees,
                ...(this.requiresCharityFields(formValues.entityType)
                    ? {
                          charityNumber: formValues.charityNumber
                              ? parseInt(formValues.charityNumber)
                              : null,
                          charityCommission: formValues.charityCommission
                              ? parseFloat(formValues.charityCommission)
                              : null
                      }
                    : {})
            }

            const formData = cleanFormDataForAPI(baseFormData)

            await this.saveCompanyDetailsWithTerminationCheck(
                formData,
                companyDetailsStore.originalCompanyDetails,
                showConfirmationCallback
            )

            if (companyDetailsStore.isAfterReset) {
                companyDetailsStore.setIsAfterReset(false)
            }
        } catch (error) {
            throw error
        } finally {
            companyDetailsStore.setIsLoading(false)
        }
    }

    async handleEntityTypeReset(formData: any, companyDetailsStore: any): Promise<void> {
        try {
            companyDetailsStore.setIsLoading(true)
            this.resetApplication()
            if (formData) {
                await this.saveCompanyDetails(formData)
            }
            companyDetailsStore.setIsAfterReset(false)
        } finally {
            companyDetailsStore.setIsLoading(false)
        }
    }

    async handleCompanySelectionReset(
        companyNumber: string,
        companyName: string,
        companyDetailsStore: any,
        handleCompanySelect: (companyNumber: string) => Promise<void>,
        getFormValues: () => any
    ): Promise<void> {
        try {
            companyDetailsStore.setIsLoading(true)
            this.resetApplication()
            await handleCompanySelect(companyNumber)
            const values = getFormValues()
            companyDetailsStore.setOriginalCompanyDetails({
                companyName: values.companyName,
                entityType: values.entityType,
                companyTradeName: values.companyTradeName,
                companyNumber: values.registrationNumber,
                dateOfCreation: values.dateOfCreation?.format('YYYY-MM-DD'),
                numberOfEmployees: values.numberOfEmployees
            })
            companyDetailsStore.clearCompanyOptions()
        } finally {
            companyDetailsStore.setIsLoading(false)
        }
    }

    shouldShowCompanySelectionConfirmation(companyDetailsStore: any): boolean {
        const hasExistingLimitedCompany =
            companyDetailsStore.originalCompanyDetails &&
            companyDetailsStore.originalCompanyDetails.entityType === BusinessStructure.COMPANY &&
            companyDetailsStore.originalCompanyDetails.companyName

        const hasExistingData =
            !!this.companyContacts ||
            (this.owners && this.owners.length > 0) ||
            (this.directors && this.directors.length > 0) ||
            !!this.bankDetails ||
            (this.stores && this.stores.length > 0)

        return hasExistingLimitedCompany && hasExistingData
    }

    isCompanyDetailsFormValid(formValues: any): boolean {
        const {
            entityType,
            customEntityType,
            companyName,
            companyTradeName,
            registrationNumber,
            numberOfEmployees,
            charityNumber,
            charityCommission
        } = formValues

        if (!entityType) return false
        if (entityType === BusinessStructure.OTHER && !customEntityType) return false
        if (!companyName || !companyTradeName) return false

        if (this.requiresCompanyNumber(entityType) && !registrationNumber) {
            return false
        }

        if (this.requiresCharityFields(entityType)) {
            if (!charityNumber || !charityCommission) {
                return false
            }
        }

        return !!numberOfEmployees
    }

    getLegalBusinessNameInfoText(entityType: string, translations: any): string {
        switch (entityType) {
            case BusinessStructure.SOLE_TRADER:
                return translations(
                    'CompanyDetails.form.legalBusinessName.searchInfoTextSoleTrader'
                )
            case BusinessStructure.COMPANY:
                return translations('CompanyDetails.form.legalBusinessName.searchInfoTextCompany')
            default:
                return translations('CompanyDetails.form.legalBusinessName.searchInfoTextOther')
        }
    }

    requiresCompanyNumber(entityType: BusinessStructure | string): boolean {
        return entityType === BusinessStructure.COMPANY || entityType === BusinessStructure.LLP
    }

    requiresRegistrationAddress(entityType: BusinessStructure | string): boolean {
        return (
            entityType === BusinessStructure.COMPANY ||
            entityType === BusinessStructure.LLP ||
            entityType === BusinessStructure.CHARITY
        )
    }

    requiresCharityFields(entityType: BusinessStructure | string): boolean {
        return entityType === BusinessStructure.CHARITY
    }

    syncRawApplicationDataWithBankAccounts(): void {
        if (this.rawApplicationData && this.bankDetails?.accounts) {
            this.rawApplicationData = {
                ...this.rawApplicationData,
                bankAccounts: this.bankDetails.accounts
            }
        }
    }

    async prefillCompanyInfoIfNeeded(): Promise<void> {
        if (this.isSoleTraderOrPartnershipFlow) {
            return
        }

        try {
            let companyDetails = this.companyDetails

            if (this.processKey) {
                await this.reloadApplicationData('prefill company info if needed')
                companyDetails = this.companyDetails
            }

            if (companyDetails?.companyNumber) {
                if (
                    !this._originalCompanyInfo ||
                    ((this._originalCompanyInfo.uberId === undefined ||
                        this._originalCompanyInfo.uberId === null) &&
                        (this._originalCompanyInfo.existingClient === undefined ||
                            this._originalCompanyInfo.existingClient === null))
                ) {
                    this._originalCompanyInfo = await this.getCompanyInfo(
                        companyDetails.companyNumber
                    )
                } else {
                    console.log('Using existing company info:', this._originalCompanyInfo)
                }

                const {directors, owners, corporateOwners} = mapCompanyInfoToOwnersAndDirectors(
                    this._originalCompanyInfo
                )

                const hasDirectorsData = this.directors.length > 0
                const hasOwnersData = this.owners.length > 0
                const hasCorporateOwnersData = this.corporateOwners.length > 0

                if (!hasDirectorsData && directors.length > 0) {
                    this.directors = directors
                    this.updateStepCompletionStatus()
                }

                if (!hasOwnersData && owners.length > 0) {
                    this.owners = owners
                    this.updateStepCompletionStatus()
                }

                if (!hasCorporateOwnersData && corporateOwners.length > 0) {
                    this.corporateOwners = corporateOwners
                    this.updateStepCompletionStatus()
                }
            }
        } catch (error) {
            console.error('Error prefilling company info:', error)
        }
    }

    getAccountLabelByNumber(accountNumber: string): string {
        const account = this.bankDetails?.accounts?.find(
            (acc) => acc.bankAccountNumber === accountNumber
        )
        if (!account) {
            return accountNumber
        }
        return `${account.bankName} (••••${account.bankAccountNumber.slice(-4)})`
    }
}
